import IdsElement from '../../core/ids-element';

import { customElement, scss } from '../../core/ids-decorators';
import { attributes, htmlAttributes } from '../../core/ids-attributes';
import { stringToBool, escapeRegExp, isValidBooleanValue } from '../../utils/ids-string-utils/ids-string-utils';
import {
  getClosestContainerNode,
  checkOverflow,
  validMaxHeight,
  getClosest
} from '../../utils/ids-dom-utils/ids-dom-utils';

import IdsDropdownAttributeMixin from './ids-dropdown-attributes-mixin';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsKeyboardMixin from '../../mixins/ids-keyboard-mixin/ids-keyboard-mixin';
import IdsFieldHeightMixin from '../../mixins/ids-field-height-mixin/ids-field-height-mixin';
import IdsColorVariantMixin from '../../mixins/ids-color-variant-mixin/ids-color-variant-mixin';
import IdsTooltipMixin from '../../mixins/ids-tooltip-mixin/ids-tooltip-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsDirtyTrackerMixin from '../../mixins/ids-dirty-tracker-mixin/ids-dirty-tracker-mixin';
import IdsFieldIndicatorMixin from '../../mixins/ids-field-indicator-mixin/ids-field-indicator-mixin';
import IdsErrorTrackerMixin from '../../mixins/ids-error-tracker-mixin/ids-error-tracker-mixin';
import IdsValidationInputMixin from '../../mixins/ids-validation-mixin/ids-validation-input-mixin';
import IdsLabelStateParentMixin from '../../mixins/ids-label-state-mixin/ids-label-state-parent-mixin';
import IdsLabelPositionMixin from '../../mixins/ids-label-position-mixin/ids-label-position-mixin';
import IdsLoadingIndicatorMixin from '../../mixins/ids-loading-indicator-mixin/ids-loading-indicator-mixin';
import IdsAutoSelectMixin from '../../mixins/ids-autoselect-mixin/ids-autoselect-mixin';
import IdsXssMixin from '../../mixins/ids-xss-mixin/ids-xss-mixin';

import '../ids-trigger-field/ids-trigger-field';
import '../ids-trigger-field/ids-trigger-button';
import '../ids-input/ids-input';
import './ids-dropdown-list';
import '../ids-list-box/ids-list-box';
import '../ids-text/ids-text';
import '../ids-icon/ids-icon';
import '../ids-color/ids-color';

import type IdsMultiselect from '../ids-multiselect/ids-multiselect';
import type IdsDropdownList from './ids-dropdown-list';
import type IdsDataGridCell from '../ids-data-grid/ids-data-grid-cell';
import type IdsTriggerButton from '../ids-trigger-field/ids-trigger-button';
import type IdsListBox from '../ids-list-box/ids-list-box';
import type IdsListBoxOption from '../ids-list-box/ids-list-box-option';
import type IdsTriggerField from '../ids-trigger-field/ids-trigger-field';
import type IdsTooltip from '../ids-tooltip/ids-tooltip';
import type IdsIcon from '../ids-icon/ids-icon';
import IdsCheckbox from '../ids-checkbox/ids-checkbox';
import type IdsSearchField from '../ids-search-field/ids-search-field';
import type IdsColor from '../ids-color/ids-color';
import debounce from '../../utils/ids-debounce-utils/ids-debounce-utils';
import { POPUP_EDGE_OFFSET } from '../ids-popup/ids-popup-attributes';
import { IdsValidationErrorMessage, IdsValidationRule } from '../../mixins/ids-validation-mixin/ids-validation-mixin';
import type { IdsLabelStateMode } from '../../mixins/ids-label-state-mixin/ids-label-state-common';

import styles from './ids-dropdown.scss';
import {
  IdsDropdownColorVariants,
  IdsDropdownOption,
  IdsDropdownOptions,
  IdsDropdownColumn
} from './ids-dropdown-common';
import { MARGIN_BLOCK_END_SIZES, MarginBlockEndKey } from '../ids-input/ids-input-attributes';
import IdsButton from '../ids-button/ids-button';

const Base = IdsDropdownAttributeMixin(
  IdsAutoSelectMixin(
    IdsErrorTrackerMixin(
      IdsDirtyTrackerMixin(
        IdsFieldIndicatorMixin(
          IdsValidationInputMixin(
            IdsLoadingIndicatorMixin(
              IdsLabelStateParentMixin(
                IdsFieldHeightMixin(
                  IdsColorVariantMixin(
                    IdsTooltipMixin(
                      IdsXssMixin(
                        IdsLocaleMixin(
                          IdsKeyboardMixin(
                            IdsEventsMixin(
                              IdsLabelPositionMixin(
                                IdsElement
                              )
                            )
                          )
                        )
                      )
                    )
                  )
                )
              )
            )
          )
        )
      )
    )
  )
);

/**
 * IDS Dropdown Component
 * @type {IdsDropdown}
 * @inherits IdsElement
 * @mixes IdsDropdownAttributeMixin
 * @mixes IdsErrorTrackerMixin
 * @mixes IdsDirtyTrackerMixin
 * @mixes IdsValidationInputMixin
 * @mixes IdsLoadingIndicatorMixin
 * @mixes IdsFieldHeightMixin
 * @mixes IdsColorVariantMixin
 * @mixes IdsTooltipMixin
 * @mixes IdsXssMixin
 * @mixes IdsLocaleMixin
 * @mixes IdsKeyboardMixin
 * @mixes IdsEventsMixin
 * @mixes IdsLabelPositionMixin
 * @mixes IdsAutoSelectMixin
 * @part dropdown - the tag element
 */
@customElement('ids-dropdown')
@scss(styles)
export default class IdsDropdown extends Base {
  isFormComponent = true;

  dropdownList?: IdsDropdownList | null;

  trigger?: IdsTriggerButton | null;

  listBox?: IdsListBox | null;

  labelClicked: boolean;

  labelEl?: HTMLLabelElement | null;

  /** Flag to prevent dropdown from opening when clearing */
  private isClearing = false;

  /** Sets to true when a keyboard operation opens the dropdown (prevents extraneous event triggering) */
  openedByKeyboard = false;

  /** Sets to true when a keyboard operation closes the dropdown (prevents extraneous event triggering) */
  closedByKeyboard = false;

  initialValueSet = false;

  /** Flag to prevent change events during initial setup */
  private isInitializing = true;

  /** Sets to true when processing the selection of the user */
  hasSelected = false;

  /** Stores the initial selectedIndex when dropdown opens (for Escape key restoration) */
  private initialSelectedIndex = 0;

  /** Flag to prevent overwriting selectedIndex during cancel operations */
  private isCancelingSelection = false;

  /** Selected option tracking for duplicate value handling */
  selectedOptionData: { id: string | null; label: string | null } | null = null;

  constructor() {
    super();
    this.state = { selectedIndex: -1 };
    this.labelClicked = false;
  }

  /**
   * Invoked each time the custom element is appended into a document-connected element.
   */
  connectedCallback() {
    const labelStateValue = this.getAttribute(attributes.LABEL_STATE);

    // Reset isInitializing for DOM reattachment scenarios so the guard is
    // active again during re-connection (same pattern as ids-tabs).
    this.isInitializing = true;

    super.connectedCallback();
    this.trigger = this.container?.querySelector('ids-trigger-button');
    this.labelEl = this.input?.labelEl;

    this.configureDropdownList();
    this.listBox = this.dropdownList?.querySelector('ids-list-box');
    if (this.maxHeight && this.listBox) {
      this.listBox.maxHeight = this.maxHeight;
    }

    this
      .#addAria()
      .#attachEventHandlers()
      .#attachKeyboardListeners();

    if (this.hasAttribute(attributes.VALUE)) this.value = this.getAttribute(attributes.VALUE);

    // Fallback: if slotchange never fires (programmatic, list attribute, no slotted content)
    // ensure isInitializing is cleared after current render cycle
    requestAnimationFrame(() => {
      this.isInitializing = false;
    });

    this.container?.classList.toggle('typeahead', this.typeahead);
    this.listBox?.setAttribute(attributes.SIZE, this.size);
    if (this.getAttribute('disabled')) this.disabled = stringToBool(this.getAttribute('disabled'));
    if (this.getAttribute('readonly')) this.readonly = stringToBool(this.getAttribute('readonly'));
    if (this.getAttribute('offset-container')) this.offsetContainer = this.getAttribute('offset-container');
    if (this.hasAttribute(attributes.TABBABLE)) this.tabbable = this.getAttribute(attributes.TABBABLE);

    // Apply label-state after input is ready
    if (labelStateValue) {
      this.labelState = labelStateValue as IdsLabelStateMode;
    }

    if (this.hasAttribute(attributes.TABBABLE)) {
      this.tabbable = this.getAttribute(attributes.TABBABLE);
    }
    if (this.hasAttribute(attributes.MAX_COLUMNS)) {
      this.maxColumns = parseInt(this.getAttribute(attributes.MAX_COLUMNS) || '10', 10);
    }
    if (this.hasAttribute(attributes.OVERFLOW)) {
      this.overflow = this.getAttribute(attributes.OVERFLOW);
    }
    if (this.hasAttribute(attributes.TOOLTIP)) {
      this.tooltip = this.getAttribute(attributes.TOOLTIP);
    }
    if (this.hasAttribute(attributes.MARGIN_BLOCK_END)) {
      this.marginBlockEnd = this.getAttribute(attributes.MARGIN_BLOCK_END) as MarginBlockEndKey;
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.dropdownList?.hide();
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes() {
    return [
      ...super.attributes,
      attributes.AUTOSELECT,
      attributes.AUTO_SIZE,
      attributes.COMBOBOX,
      attributes.DISABLE_TOOLTIP,
      attributes.ID,
      attributes.INPUT_AUTO_SIZE,
      attributes.LIST_BOX_POSITION_STYLE,
      attributes.MAX_COLUMNS,
      attributes.MAX_HEIGHT,
      attributes.MULTI_COLUMN,
      attributes.NO_SEARCH,
      attributes.OFFSET_CONTAINER,
      attributes.OVERFLOW,
      attributes.SHOW_VALUE,
      attributes.SKIP_SANITIZE,
      attributes.REQUIRED,
      attributes.MARGIN_BLOCK_END,
      attributes.TABBABLE,
      attributes.TOOLTIP,
      attributes.VALIDATE
    ];
  }

  optionsData: IdsDropdownOptions = [];

  columnsData: IdsDropdownColumn[] = [];

  private maxColumnsValue: number = 10;

  #isMultiSelect: boolean = this.nodeName === 'IDS-MULTISELECT';

  protected typeaheadInput: string | null = null;

  protected valueBeforeSearch: string | null | undefined = undefined;

  private noResultsSelected = false;

  /**
   * Check if dropdown options contain icons
   * @returns {boolean} true if icons are present
   */
  get hasIcons(): boolean {
    return this.querySelector('ids-list-box-option ids-icon') !== null;
  }

  /**
   * Check if dropdown options contain avatars
   * @returns {boolean} true if avatars are present
   */
  get hasAvatar(): boolean {
    return this.querySelector('ids-list-box-option ids-avatar') !== null;
  }

  /**
   * List of available color variants for this component
   * @returns {Array<string>}
   */
  colorVariants: Array<string> = IdsDropdownColorVariants;

  /**
   * Adds validation message(s) to the component.
   * If the popup is visible and no item is selected, it removes the validation message instead.
   * @param {Array<IdsValidationErrorMessage> | IdsValidationErrorMessage} message - The validation message(s) to be added
   * @returns {void}
   */
  addValidationMessage(message: Array<IdsValidationErrorMessage> | IdsValidationErrorMessage): void {
    if (this.popup?.hasAttribute('visible') && !this.hasSelected) {
      super.removeValidationMessage(message);
      return;
    }

    super.addValidationMessage(message);
  }

  /**
   * Add custom validation rule for dropdown
   * @param {Array<IdsValidationRule> | IdsValidationRule} rule Rule settings
   */
  addValidationRule(rule: Array<IdsValidationRule> | IdsValidationRule) {
    if (this.input) this.input.addValidationRule(rule);
  }

  /**
   * Push color variant to the container element
   * @returns {void}
   */
  onColorVariantRefresh(): void {
    if (this.input) this.input.colorVariant = this.colorVariant;
    if (this.dropdownList) {
      this.dropdownList.colorVariant = this.colorVariant;
      if (this.dropdownList.popup) {
        this.dropdownList.popup.type = this.colorVariant !== null ? this.colorVariant : 'dropdown';
      }
    }
  }

  /**
   * Handle changes to the clearable attribute
   */
  onClearableChange(): void {
    if (this.input) {
      this.input.clearableForced = this.clearable ? 'true' : 'false';
    }
  }

  /**
   * Handle changes to the label attribute
   */
  onLabelChange(): void {
    if (this.input) this.input.label = this.label;
  }

  /**
   * Push label-state to the container element
   * @returns {void}
   */
  onLabelStateChange(): void {
    if (this.input) {
      // Wait for IdsTriggerField to be fully upadated
      if (this.input.constructor.name === 'HTMLElement') {
        setTimeout(() => this.onLabelStateChange(), 0);
        return;
      }
      this.input.labelState = this.labelState;
    }
  }

  /**
   * Push label-wrap to the container element
   * @returns {void}
   */
  onLabelWrapChange(): void {
    this.updateLabelWrapForCollapsedState();
  }

  /**
   * Push label-position to the container element
   * @returns {void}
   */
  onLabelPositionChange(): void {
    if (this.input) this.input.labelPosition = this.labelPosition;
  }

  /**
   * Push label-width to the container element
   * @returns {void}
   */
  onLabelWidthChange(): void {
    if (this.input) this.input.labelWidth = this.labelWidth;
  }

  /**
   * Push label-align-y to the container element
   * @returns {void}
   */
  onLabelAlignYChange(): void {
    if (this.input) this.input.labelAlignY = this.labelAlignY;
  }

  /**
   * Push label-alignment to the container element
   * @returns {void}
   */
  onLabelAlignmentChange(): void {
    if (this.input) this.input.labelAlignment = this.labelAlignment;
  }

  /**
   * Push label-break to the container element
   * @returns {void}
   */
  onLabelBreakChange(): void {
    if (this.input) this.input.labelBreak = this.labelBreak;
  }

  /**
   * Handle changes to the label required state
   */
  onLabelRequiredChange(): void {
    if (this.input) this.input.labelRequired = this.labelRequired;
  }

  /**
   * Push field-height/compact to the container element
   * @param {string} val the new field height setting
   */
  onFieldHeightChange(val: string) {
    if (val) {
      const attr = val === attributes.COMPACT ? { name: attributes.COMPACT, val: '' } : { name: attributes.FIELD_HEIGHT, val };
      this.input?.setAttribute(attr.name, attr.val);
      this.dropdownList?.setAttribute(attr.name, attr.val);
    } else {
      this.input?.removeAttribute(attributes.COMPACT);
      this.input?.removeAttribute(attributes.FIELD_HEIGHT);
      this.dropdownList?.removeAttribute(attributes.COMPACT);
      this.dropdownList?.removeAttribute(attributes.FIELD_HEIGHT);
    }
  }

  /**
   * Handle changes to the id attribute
   * @param {string} value The new id value
   */
  onIdChange(value: string) {
    if (this.shadowRoot) {
      const templateId = value && value !== 'none' ? value : '';
      const triggerField = this.shadowRoot.querySelector('ids-trigger-field');
      const triggerButton = this.shadowRoot.querySelector('ids-trigger-button');
      const dropdownList = this.shadowRoot.querySelector('ids-dropdown-list');

      if (triggerField) triggerField.id = `trigger-field-${templateId}`;
      if (triggerButton) triggerButton.id = templateId ? `trigger-btn-${templateId}` : '';
      if (dropdownList) dropdownList.id = templateId ? `dropdownList-${templateId}` : '';
    }
  }

  /**
   * Create the Template for the contents
   * @returns {string} The template
   */
  template(): string {
    const colorVariant = this.colorVariant ? ` color-variant="${this.colorVariant}"` : '';
    const fieldHeight = this.fieldHeight ? ` field-height="${this.fieldHeight}"` : '';
    const labelState = this.labelState ? ` label-state="${this.labelState}"` : '';
    const labelPosition = this.labelPosition ? ` label-position="${this.labelPosition}"` : '';
    const labelWidth = this.labelWidth ? ` label-width="${this.labelWidth}"` : '';
    const labelAlignY = this.labelAlignY ? ` label-align-y="${this.labelAlignY}"` : '';
    const labelAlignment = this.labelAlignment ? ` label-alignment="${this.labelAlignment}"` : '';
    const labelWrap = this.labelWrap ? ` label-wrap="${this.labelWrap}"` : '';
    const compact = this.compact ? ' compact' : '';
    const noMargins = this.noMargins ? ' no-margins' : '';
    const builtInDropdown = !this.list ? this.templateDropdownList() : '';
    const cssParts = 'container: triggerfield-container, field-container: triggerfield-field-container, content-area: triggerfield-content-area, input: triggerfield-input, popup: triggerfield-popup';
    const templateId = this.id && this.id !== 'none' ? this.id : '';
    const overflow = this.overflow ? ` overflow="${this.overflow}"` : '';
    const tooltip = this.tooltip ? ` tooltip="${this.tooltip}"` : '';

    return `
    <div class="ids-dropdown" part="container">
      <ids-trigger-field
        id="trigger-field-${templateId}"
        ${!this.typeahead && !this.disabled ? ' ' : ''}
        ${this.disabled ? ' disabled="true"' : ''}
        ${this.readonly ? '' : ' readonly-background'}
        ${!this.readonly && !this.disabled ? '' : ' cursor="pointer"'}
        size="${this.size}"
        auto-size="${this.inputAutoSize}"
        label="${this.label}"
        placeholder="${this.placeholder}"
        part="trigger-field"
        exportparts="${cssParts}"
        ${this.comboBox ? ' combobox="true"' : ''}
        ${this.clearable ? ' clearable-forced="true"' : ''}
        ${colorVariant}${fieldHeight}${compact}${noMargins}${labelState}${labelPosition}${labelWidth}${labelAlignY}${labelAlignment}${labelWrap}${overflow}${tooltip}
        ${this.labelBreak ? ` label-break="${this.labelBreak}"` : ''}
        ${this.validate ? ` validate="${this.validate}"` : ''}
        ${this.validate && this.validationEvents ? ` validation-events="${this.validationEvents}"` : ''}
        ${this.errorTracker ? ' error-tracker="true"' : ''}
      >
        <ids-trigger-button
          class="ids-trigger-field-slot-trigger-end"
          ${templateId ? `id="trigger-btn-${templateId}"` : ''}
          slot="trigger-end"
          part="trigger-button"
          tabbable="false"
          disabled="${this.disabled}"
        >
          <ids-text audible="true" translate-text="true">DropdownTriggerButton</ids-text>
          <ids-icon icon="dropdown" part="icon"></ids-icon>
        </ids-trigger-button>
      </ids-trigger-field>
      ${builtInDropdown}
    </div>`;
  }

  private templateDropdownList(): string {
    const fieldHeight = this.fieldHeight && this.fieldHeight !== 'compact' ? ` field-height="${this.fieldHeight}"` : '';
    const compact = this.compact || this.fieldHeight === 'compact' ? ' compact' : '';
    const size = this.size ? ` size="${this.size}"` : '';
    const value = this.value ? ` value="${this.value}"` : '';
    const noMargins = this.noMargins ? ' no-margins' : '';
    const autoSize = this.autoSize ? ` auto-size="${this.autoSize}"` : '';
    const templateId = this.id && this.id !== 'none' ? this.id : '';

    return `<ids-dropdown-list
      ${this.comboBox ? 'class="is-combobox"' : ''}
      ${templateId ? `id="dropdownList-${templateId}"` : ''}
      ${fieldHeight}
      ${compact}
      ${size}
      ${value}
      ${noMargins}
      ${autoSize}
      tabindex="-1"
      position-style="fixed">
      <slot></slot>
    </ids-dropdown-list>`;
  }

  /**
   * Add internal aria attributes
   * @private
   * @returns {object} This API object for chaining
   */
  #addAria() {
    const targetListboxId = this.listBox?.getAttribute(attributes.ID) || `ids-list-box-${this.id}`;

    const attrs: any = {
      role: 'combobox',
      'aria-expanded': 'false',
      'aria-autocomplete': 'list',
      'aria-haspopup': 'listbox',
      'aria-label': this.label || '',
      'aria-controls': targetListboxId
    };

    this.dropdownList?.listBox?.setAttribute(attributes.ID, targetListboxId);
    this.dropdownList?.listBox?.setAttribute(htmlAttributes.ARIA_LABEL, `Listbox`);

    Object.keys(attrs).forEach((key: any) => this.setAttribute(key, attrs[key]));
    return this;
  }

  /**
   * Sets allow-blank setting
   * @param {string|boolean} value adds blank option if true
   */
  set allowBlank(value: boolean | string) {
    const truthyValue = isValidBooleanValue(value) && stringToBool(value);
    const dropdownList = this.shadowRoot?.querySelector('.ids-dropdown ids-dropdown-list');
    if (truthyValue) {
      this.setAttribute(attributes.ALLOW_BLANK, 'true');

      if (this.dropdownList) this.dropdownList.allowBlank = true;
      else if (dropdownList) dropdownList.setAttribute(attributes.ALLOW_BLANK, 'true');
    } else {
      this.removeAttribute(attributes.ALLOW_BLANK);
      if (this.dropdownList) this.dropdownList.allowBlank = false;
      else if (dropdownList) dropdownList.removeAttribute(attributes.ALLOW_BLANK);
    }

    if (typeof this.onAllowBlankChange === 'function') this.onAllowBlankChange(truthyValue);
  }

  /**
   * Gets allow-blank value
   * @returns {boolean} allow-blank value
   */
  get allowBlank(): boolean {
    return stringToBool(this.getAttribute(attributes.ALLOW_BLANK));
  }

  /**
   * Get the input element
   * @returns {IdsTriggerField | null} the trigger field element
   */
  get input(): IdsTriggerField | null {
    return this.container?.querySelector<IdsTriggerField>('ids-trigger-field') ?? null;
  }

  /**
   * Get the popup element
   * @returns {any} the popup element
   */
  get popup() {
    return this.dropdownList?.popup;
  }

  /**
   * Sets whether the dropdown displays options in multiple columns
   * @param {boolean} value If true will set `multi-column` attribute
   */
  set multiColumn(value: boolean) {
    this.toggleAttribute(attributes.MULTI_COLUMN, isValidBooleanValue(value) && stringToBool(value));
  }

  /**
   * Get whether the dropdown displays options in multiple columns
   * @returns {boolean} true if multi-column is enabled
   */
  get multiColumn(): boolean {
    return this.hasAttribute(attributes.MULTI_COLUMN);
  }

  /**
   * Sets whether to show the value instead of the label text in the dropdown
   * @param {boolean} value If true will set `show-value` attribute
   */
  set showValue(value: boolean) {
    this.toggleAttribute(attributes.SHOW_VALUE, isValidBooleanValue(value) && stringToBool(value));
  }

  /**
   * Get whether to show the value instead of the label text
   * @returns {boolean} true if show-value is enabled
   */
  get showValue(): boolean {
    return this.hasAttribute(attributes.SHOW_VALUE);
  }

  /**
   * Set the value of the dropdown and reset dirty tracking
   * @param {string} value The value/id to use
   */
  set valueResetTracking(value: string | null | undefined) {
    this.value = value;
    this.resetDirtyTracker();
  }

  /**
   * Set the value of the dropdown using the value/id attribute if present
   * @param {string} value The value/id to use
   */
  set value(value: string | null | undefined) {
    // Sync stored option IDs from dataset FIRST, before any other logic
    this.#syncStoredOptionIds();

    if (this.allowBlank && (value === 'null' || value === null || value === undefined)) {
      this.value = 'blank';
      this.#syncInputTextWithOption('blank');
      return;
    }

    value = (Array.isArray(value) ? value[0] : value) || String(value ?? '');
    const labels = this.labels;
    const label = String(value);

    if (labels.includes(label)) {
      value = this.optionValues[labels.indexOf(label)];
    }

    // Store current value before sync to prevent clearing selectedOptionData
    const currentValue = this.getAttribute(attributes.VALUE);
    const isSameValue = currentValue === value;

    if (this.comboBox) this.#syncComboTextWithOption(value);
    else this.#syncInputTextWithOption(value);
    this.setAttribute(attributes.VALUE, value ?? '');

    // clear selectedOptionData when value actually changes to empty
    if ((!value || value === '') && !isSameValue) {
      this.selectedOptionData = null;
    }
  }

  /**
   * Get the current value of the dropdown
   * @returns {string | null | undefined} the current value
   */
  get value(): string | null | undefined {
    return this.getAttribute(attributes.VALUE);
  }

  /**
   * Set the auto-size of input
   * @param {string} value The value of auto-size
   */
  set autoSize(value: string) {
    const boolVal = isValidBooleanValue(value) && stringToBool(value);
    if (boolVal) {
      this.setAttribute(attributes.AUTO_SIZE, 'true');
    } else {
      this.removeAttribute(attributes.AUTO_SIZE);
    }
  }

  /**
   * Get the auto-size setting
   * @returns {boolean} true if auto-size is enabled
   */
  get autoSize(): boolean {
    return stringToBool(this.getAttribute(attributes.AUTO_SIZE));
  }

  /**
   * Set the input-auto-size setting
   * @param {string|boolean} value The value of input-auto-size
   */
  set inputAutoSize(value: string | boolean) {
    const boolVal = isValidBooleanValue(value) && stringToBool(value);
    if (boolVal) {
      this.setAttribute(attributes.INPUT_AUTO_SIZE, 'true');
    } else {
      this.setAttribute(attributes.INPUT_AUTO_SIZE, 'false');
    }
  }

  /**
   * Get the input-auto-size setting
   * @returns {boolean} true if input auto-size is enabled (default true)
   */
  get inputAutoSize(): boolean {
    const attr = this.getAttribute(attributes.INPUT_AUTO_SIZE);
    return attr === null ? true : stringToBool(attr);
  }

  /**
   * Sets the positioning style of the listbox popup
   * @param {('fixed'|'absolute'|null)} val The positioning style - 'fixed' or 'absolute'
   */
  set listBoxPositionStyle(val: 'fixed' | 'absolute' | null) {
    if (!val || !['fixed', 'absolute'].includes(val)) {
      return;
    }

    this.setAttribute(attributes.LIST_BOX_POSITION_STYLE, val);
    this.dropdownList?.setAttribute(attributes.POSITION_STYLE, val);
  }

  /**
   * Get the positioning style of the listbox popup
   * @returns {'fixed' | 'absolute'} the positioning style
   */
  get listBoxPositionStyle(): 'fixed' | 'absolute' {
    return this.getAttribute(attributes.LIST_BOX_POSITION_STYLE) === 'fixed' ? 'fixed' : 'absolute';
  }

  /**
   * Get all available option-values for the dropdown
   * @returns {string[]} array of option values
   */
  get optionValues(): string[] {
    return this.options.map((item) => item.value ?? '');
  }

  /**
   * Get all available labels of the dropdown
   * @returns {string[]} array of labels
   */
  get labels(): string[] {
    return this.options.map((item) => item.innerText ?? item.textContent ?? '');
  }

  /**
   * Get the selected Listbox option based on the Dropdown's value
   * @returns {HTMLElement | null} the selected option
   */
  get selectedOption(): HTMLElement | null {
    const selector = this.generateSelectorForListBoxOption(this.value || '');
    const matchingOptions = this.dropdownList?.listBox?.querySelectorAll(`${selector}:not([value=""])`);

    if (!matchingOptions || matchingOptions.length === 0) return null;
    if (matchingOptions.length === 1) return matchingOptions[0] as HTMLElement;

    const cell = this.closest('ids-data-grid-cell') as IdsDataGridCell;
    const optionId = this.selectedOptionData?.id || cell?.dataset.selectedOptionId;
    const optionLabel = this.selectedOptionData?.label || cell?.dataset.selectedOptionLabel;

    if (optionId || optionLabel) {
      const match = Array.from(matchingOptions).find((opt) => opt.getAttribute('id') === optionId || opt.textContent?.trim() === optionLabel);
      if (match) return match as HTMLElement;
    }

    return matchingOptions[0] as HTMLElement;
  }

  /**
   * Get the currently-selected Listbox option
   * @returns {IdsListBoxOption | null} reference to a selected Listbox option if one is present
   */
  get selected(): IdsListBoxOption | null {
    return this.dropdownList?.selected || null;
  }

  /**
   * Set the selected option by index
   * @param {number} value the index to use
   */
  set selectedIndex(value: number) {
    if (Number.isInteger(value) && this.options[value]) {
      const elem = this.options[value];
      this.value = elem.getAttribute(attributes.VALUE);
      this.state.selectedIndex = value;
    }
  }

  /**
   * Get the selected option index
   * @returns {number} the selected index
   */
  get selectedIndex() { return this.state.selectedIndex; }

  /**
   * Get the currently available options
   * @returns {Array<IdsListBoxOption>} the array of options
   */
  get options() {
    return [...this.dropdownList?.listBox?.querySelectorAll<IdsListBoxOption>('ids-list-box-option') ?? []];
  }

  /**
   * Sets the readonly attribute
   * @param {string|boolean} value string value from the readonly attribute
   */
  set readonly(value) {
    const isReadonly = isValidBooleanValue(value) && stringToBool(value);

    if (isReadonly) {
      this.removeAttribute(attributes.DISABLED);
      if (this.input) {
        this.input.disabled = false;
        this.input.cursor = 'text';
        this.input.readonlyBackground = false;
        this.input.readonly = true;
      }
      this.setAttribute(attributes.READONLY, 'true');
      return;
    }
    if (this.input) {
      this.input.disabled = false;
      this.input.cursor = 'pointer';
      this.input.readonlyBackground = true;

      // When typeahead is enabled or combobox is enabled, allow user input
      if (this.typeahead || this.comboBox) {
        this.input.readonly = false;
      }
    }
    this.removeAttribute(attributes.READONLY);
  }

  /**
   * Get the readonly state
   * @returns {boolean} true if readonly
   */
  get readonly() {
    return stringToBool(this.getAttribute(attributes.READONLY)) || false;
  }

  /**
   * Sets the disabled attribute
   * @param {boolean | string} value string value from the disabled attribute
   */
  set disabled(value: boolean | string) {
    const isDisabled = isValidBooleanValue(value) && stringToBool(value);
    if (isDisabled) {
      this.removeAttribute(attributes.READONLY);
      if (this.input) {
        this.input.disabled = true;
        this.input.readonly = false;
        this.input.cursor = 'initial';
        this.input.bgTransparent = false;
      }
      this.setAttribute(attributes.DISABLED, 'true');
      return;
    }

    if (this.input) {
      this.input.disabled = false;
      this.input.cursor = 'pointer';
      this.input.bgTransparent = true;
    }

    this.removeAttribute(attributes.DISABLED);
  }

  /**
   * Get the disabled state
   * @returns {boolean | string} true if disabled
   */
  get disabled(): boolean | string {
    return stringToBool(this.getAttribute(attributes.DISABLED)) || false;
  }

  /**
   * Get the max height value
   * @returns {string | null} the max height value
   */
  get maxHeight(): string | null {
    return this.getAttribute(attributes.MAX_HEIGHT);
  }

  /**
   * Set the max height value
   * @param {string | number | null} value The value
   */
  set maxHeight(value: string | number | null) {
    const val = validMaxHeight(value);
    if (val) {
      this.setAttribute(attributes.MAX_HEIGHT, val);
    } else {
      this.removeAttribute(attributes.MAX_HEIGHT);
    }

    if (this.popup) this.popup.maxHeight = value;
  }

  /**
   * Sets whether to disable tooltip
   * @param {string | boolean | null} val boolean flag
   */
  set disableTooltip(val: string | boolean | null) {
    const isDisabled = isValidBooleanValue(val) && stringToBool(val);
    this.toggleAttribute(attributes.DISABLE_TOOLTIP, isDisabled);
    this.input?.toggleAttribute(attributes.DISABLE_TOOLTIP, isDisabled);
  }

  /**
   * Get whether tooltip is disabled
   * @returns {boolean} true if tooltip is disabled
   */
  get disableTooltip(): boolean {
    return stringToBool(this.getAttribute(attributes.DISABLE_TOOLTIP));
  }

  /**
   * Sets the overflow behavior for text content
   * @param {string | null} value The overflow value (e.g., 'ellipsis')
   */
  set overflow(value: string | null) {
    if (value) {
      this.setAttribute(attributes.OVERFLOW, value);
      this.input?.setAttribute(attributes.OVERFLOW, value);
    } else {
      this.removeAttribute(attributes.OVERFLOW);
      this.input?.removeAttribute(attributes.OVERFLOW);
    }
  }

  /**
   * Get the overflow behavior
   * @returns {string | null} the overflow value
   */
  get overflow(): string | null {
    return this.getAttribute(attributes.OVERFLOW);
  }

  /**
   * Sets the tooltip text or behavior
   * @param {string | boolean | null} value The tooltip text or true for auto tooltip
   */
  set tooltip(value: string | boolean | null) {
    if (value === true || value === 'true') {
      this.setAttribute(attributes.TOOLTIP, 'true');
    } else if (value && typeof value === 'string') {
      this.setAttribute(attributes.TOOLTIP, value);
    } else {
      this.removeAttribute(attributes.TOOLTIP);
    }
  }

  /**
   * Get the tooltip text or behavior
   * @returns {string} the tooltip value
   */
  get tooltip(): string {
    return this.getAttribute(attributes.TOOLTIP) || '';
  }

  /**
   * Handle changes to the allow blank setting
   * @param {boolean} val - Whether blank options are allowed
   */
  onAllowBlankChange(val: boolean) {
    if (this.dropdownList) this.dropdownList.allowBlank = val;
    if (!val && this.value === 'blank') this.removeAttribute(attributes.VALUE);
  }

  /**
   * Set the aria and state on the element
   * @param {HTMLElement} option the option to select
   * @private
   */
  selectOption(option: IdsListBoxOption) {
    if (this.dropdownList) {
      this.dropdownList.selectOption(option);
    }
  }

  /**
   * Removes selected attributes from an option
   * @param {HTMLElement} option element to remove attributes
   */
  deselectOption(option: HTMLElement | undefined | null) {
    this.dropdownList?.deselectOption(option);
  }

  /**
   * Get the currently-displayed IdsIcon in the trigger field
   * @returns {IdsIcon | null} the dropdown icon element
   */
  get dropdownIconEl(): IdsIcon | null {
    return this.input?.querySelector<IdsIcon>('ids-icon[slot="trigger-start"]') || null;
  }

  /**
   * Get the dropdown color swatch
   * @returns {IdsColor | null} the color swatch element
   */
  get dropdownColorSwatch(): IdsColor | null {
    return this.input?.querySelector<IdsColor>('ids-color[slot="trigger-start"]') || null;
  }

  /**
   * Set the icon to be visible (if used)
   * @private
   * @param {HTMLElement} option the option to select
   */
  selectIcon(option: HTMLElement | undefined | null) {
    // Handle module nav avatar/icon selection
    if (this.closest('ids-module-nav') && this.hasAvatar) {
      const moduleNavButton = this.closest('ids-module-nav')?.querySelector('#module-nav-button');
      const optionAvatar = option?.querySelector('ids-avatar');
      const optionIcon = option?.querySelector<IdsIcon>('ids-icon');

      if (moduleNavButton) {
        // Remove existing icon/avatar
        moduleNavButton.querySelector('ids-icon')?.remove();
        moduleNavButton.querySelector('ids-avatar')?.remove();

        // Add new avatar or icon
        if (optionAvatar) {
          const avatarClone = optionAvatar.cloneNode(true) as HTMLElement;
          moduleNavButton.insertBefore(avatarClone, moduleNavButton.querySelector('ids-text'));
        } else if (optionIcon) {
          const iconClone = optionIcon.cloneNode(true) as HTMLElement;
          moduleNavButton.insertBefore(iconClone, moduleNavButton.querySelector('ids-text'));
        }
      }
      return;
    }

    const dropdownIcon = this.dropdownIconEl || this.dropdownColorSwatch;
    if (!dropdownIcon && !option) return;

    const showListItemIcon = this.showListItemIcon || this.hasIcons;

    if (!this.hasIcons && !showListItemIcon) {
      dropdownIcon?.remove();
      return;
    }

    const optionIcon = option?.querySelector<IdsIcon>('ids-icon');
    const optionColor = option?.querySelector<IdsColor>('ids-color');

    if (!optionIcon && !optionColor) return;

    if (!dropdownIcon && showListItemIcon) {
      // create trigger field icon if dne
      if (optionIcon) {
        const iconColor = optionIcon.getAttribute(attributes.DATA_OG_COLOR) || optionIcon.color;
        this.generateIcon(optionIcon.icon, iconColor as string | undefined);
      }
      if (optionColor) this.generateColorSwatch(optionColor.hex, optionColor.color as string);
    } else {
      // replace existing trigger field icon/swatch
      if (optionIcon) {
        dropdownIcon?.setAttribute(attributes.ICON, optionIcon.icon);
        const iconColor = optionIcon.getAttribute(attributes.DATA_OG_COLOR) || optionIcon.color;
        dropdownIcon?.setAttribute(attributes.COLOR, `${iconColor || ''}`);
      }

      if (optionColor) {
        if (optionColor.hex) dropdownIcon?.setAttribute(attributes.HEX, optionColor.hex);
        else dropdownIcon?.setAttribute(attributes.COLOR, optionColor.color ?? '');
      }
    }
  }

  /**
   * Creates and appends the IdsIcon for the dropdown list's trigger button
   * @param {string} val name of the IdsIcon to use
   * @param {string} color color of the IdsIcon.
   */
  private generateIcon(val: string, color: string = 'black') {
    // Don't generate icons for module nav dropdowns
    if (this.closest('ids-module-nav')) {
      return;
    }
    this.input?.insertAdjacentHTML('beforeend', `<ids-icon slot="trigger-start" class="trigger-icon" icon="${val}" color= "${color}"></ids-icon>`);
  }

  /**
   * Creates and appends the IdsColor for the dropdown list's trigger button
   * @param {string} hexCode Hex value of the IdsColor to use
   * @param {string} color Color value of the IdsColor to use
   */
  private generateColorSwatch(hexCode: string | null, color: string | null) {
    const colorAttr = hexCode ? `hex="${hexCode}"` : `color="${color}"`;
    this.input?.insertAdjacentHTML('beforeend', `<ids-color slot="trigger-start" class="trigger-icon" ${colorAttr}></ids-color>`);
  }

  /**
   * Set the tooltip to be visible for the selected option
   * @private
   * @param {HTMLElement} option the option to select
   */
  selectTooltip(option: HTMLElement | undefined | null) {
    if (!option) {
      this.removeAttribute(attributes.TOOLTIP);
      if (this.tooltipEl) this.tooltipEl.target = null;
      return;
    }

    // Check if this is the "No Results" option
    const isNoResults = option.textContent?.trim() === this.localeAPI.translate('NoResults');
    let tooltipText;
    const tooltip = option?.getAttribute('tooltip');
    if (tooltip) {
      tooltipText = tooltip;
    } else if (isNoResults) {
      tooltipText = '';
    } else {
      const slotEl = option.shadowRoot?.querySelector('slot');
      tooltipText = this.multiColumn && slotEl
        ? (slotEl.assignedElements()[1])?.innerHTML ?? ''
        : option && ((option.querySelector('span')?.textContent?.trim() || '') || (option.textContent?.trim() || ''));
    }

    // Only change the tooltip content if its set to true
    this.tooltip = tooltipText;
    if (tooltipText) {
      this.setAttribute(attributes.TOOLTIP, tooltipText);
    } else {
      this.removeAttribute(attributes.TOOLTIP);
    }

    if (this.tooltipEl) this.tooltipEl.target = option;
  }

  /**
   * Remove the aria and state from the currently selected element
   */
  clearSelected() {
    this.dropdownList?.clearSelected();
  }

  /**
   * Configures the Dropdown component's attached IdsDropdownList/IdsPopup
   */
  configurePopup() {
    if (!this.dropdownList?.popup || !this.trigger) return;

    this.dropdownList.removeTriggerEvents();
    this.dropdownList.appendToTargetParent();

    if (!this.inputAutoSize) {
      this.dropdownList.classList.add('input-auto-size-false');
    } else {
      this.dropdownList.classList.remove('input-auto-size-false');
    }

    if (this.dropdownList?.popup) {
      this.dropdownList.popup.onOutsideClick = this.onOutsideClick.bind(this);
    }
    this.dropdownList.onTriggerClick = () => {
      if (this.input?.input?.matches(':focus')) this.labelClicked = false;
      if (this.labelClicked) {
        this.labelClicked = false;
        return;
      }
      if (!this.disabled && !this.readonly) {
        this.toggle(this.typeahead);
      }
    };

    // Associate the Dropdown List component with this Dropdown component's trigger button
    const targetElemId = (this.list ? this : this.input?.input)?.getAttribute(attributes.ID) || (this.id ? `trigger-field-${this.id}-internal` : 'trigger-field-internal');
    const triggerElemId = (this.list ? this : this.trigger)?.getAttribute(attributes.ID) || (this.id ? `trigger-btn-${this.id}` : 'triggerBtn');

    this.dropdownList.setAttribute(attributes.TARGET, `#${targetElemId}`);
    this.dropdownList.setAttribute(attributes.TRIGGER_ELEM, `#${triggerElemId}`);
    if (this.dropdownList?.popup) {
      this.dropdownList.popup.alignTarget = this.input?.fieldContainer || this.dropdownList || this;
    }

    // Adjust popup position if validation message is visible
    if (this.dropdownList?.popup) {
      // Check if we're inside an action panel
      const isInActionPanel = !!this.closest('ids-action-panel');

      if (isInActionPanel) {
        this.onEvent('show', this.dropdownList.popup, () => {
          const popupElement = this.dropdownList?.popup?.shadowRoot?.querySelector('.ids-popup.dropdown.position-fixed');
          if (popupElement) {
            (popupElement as HTMLElement).style.bottom = '';
          }
        }, { once: true });

        // Set up mutation observer to watch for validation message changes
        const observer = new MutationObserver(() => {
          const popupElement = this.dropdownList?.popup?.shadowRoot?.querySelector('.ids-popup.dropdown.position-fixed.open');
          const validationMessage = this.input?.shadowRoot?.querySelector('.validation-message');

          if (popupElement) {
            if (validationMessage && window.getComputedStyle(validationMessage).display !== 'none') {
              const validationHeight = validationMessage.getBoundingClientRect().height;
              (popupElement as HTMLElement).style.bottom = `${validationHeight + 4}px`;
            } else {
              (popupElement as HTMLElement).style.bottom = '';
            }
          }
        });

        if (this.input?.shadowRoot) {
          observer.observe(this.input.shadowRoot, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class']
          });
        }
        this.onEvent('hide', this.dropdownList.popup, () => {
          observer.disconnect();
        }, { once: true });
      }
    }

    // Configure inner IdsPopup
    const isRTL = this.localeAPI.isRTL();
    this.dropdownList.popup.align = `bottom, ${isRTL || ['lg', 'full'].includes(this.size) ? 'right' : 'left'}`;
    this.dropdownList.popup.alignEdge = 'bottom';

    if (this.input) this.dropdownList.value = this.input.value;
    if (this.#isMultiSelect) this.dropdownList.isMultiSelect = true;
  }

  /**
   * Handle clicks outside the dropdown popup
   * @param {Event} e - The click event
   */
  onOutsideClick(e: Event) {
    if (this.dropdownList && this.dropdownList.popup?.visible) {
      const path = e.composedPath();
      if (!path?.includes(this.dropdownList) && !path?.includes(this.input?.fieldContainer || this)) {
        const inputText = this.input?.value?.trim() || '';
        if (inputText && this.value) {
          const matchingOption = this.#findMatchingOption(inputText);

          if (!matchingOption) {
            this.#syncInputTextWithOption(this.value);
          }
        }
        // Restore original selectedIndex before closing (same as Escape key fix)
        this.isCancelingSelection = true;
        if (this.initialSelectedIndex !== undefined && this.initialSelectedIndex !== null) {
          this.state.selectedIndex = this.initialSelectedIndex;
        }
        this.close(true);
        this.isCancelingSelection = false;
        this.initialSelectedIndex = 0; // Reset after use
      }
    }
  }

  #searchValue: string | null = null;

  /**
   * Attach events for combobox
   */
  #attachComboboxEvents() {
    // Refactored Implementation for Combobox with proper segregation to normal dropdown functionality
    if (this.comboBox || this.noSearch) {
      this.offEvent('input.dropdown', this.input?.input);
      this.onEvent('input.dropdown', this.input?.input, (e: any) => {
        const target = e.target.value.toString().toLowerCase();
        const selectedOption = this.options.find((opt) => opt.classList.contains('is-selected'));
        const matchingOption = this.#findMatchingOption(target);

        if (selectedOption) this.deselectOption(selectedOption);
        if (matchingOption) {
          this.selectOption(matchingOption);
        } else {
          this.state.selectedIndex = -1;
        }

        this.input?.input?.focus();
        requestAnimationFrame(() => {
          if (this.input?.input) {
            const length = this.input.input.value.length;
            this.input.input.setSelectionRange(length, length);
          }
        });

        if (this.typeahead) this.#typeAhead(target);
      });
    }
  }

  /**
   * Attach events for typeahead
   */
  #attachTypeaheadEvents() {
    this.offEvent('input.dropdown', this.input?.input);
    if (this.typeahead && typeof this.state.beforeShow === 'function') {
      this.onEvent('input.dropdown', this.input?.input, debounce(async (e: any) => {
        if (e.target?.input?.value !== null) this.#searchValue = e.target?.input?.value;
        const data = await this.state.beforeShow({
          typeahead: this.#searchValue
        });

        if (this.allowBlank && !data.some((d: { value: string; }) => d.value === 'blank')) {
          data.unshift({ id: 'blank-id', value: 'blank', label: '' });
        }

        if (data) {
          this.loadDataSet(data);
          if (this.typeahead) {
            this.optionsData = data;
          }
        }
        if (this.#searchValue !== null) this.#typeAhead(this.#searchValue);
      }, 410));
    } else {
      this.onEvent('input.dropdown', this.input?.input, (e: any) => {
        if (this.container?.classList.contains('is-open')) {
          if (this.typeahead) this.#typeAhead(e.target?.value);
          else this.#selectMatch(e.target?.value);
        }
      });
    }
  }

  /**
   * Syncs option IDs from dataset to instance properties
   * @private
   */
  #syncStoredOptionIds() {
    const cell = this.closest('ids-data-grid-cell') as IdsDataGridCell;
    if (cell?.dataset.selectedOptionId && !this.selectedOptionData?.id) {
      this.selectedOptionData = {
        id: cell.dataset.selectedOptionId || this.selectedOptionData?.id || null,
        label: cell.dataset.selectedOptionLabel || this.selectedOptionData?.label || null
      };
    }
  }

  /**
   * Open the dropdown list
   * @param {boolean} shouldSelect whether or not the input text should be selected
   */
  async open(shouldSelect = false) {
    if (!this.dropdownList || this.disabled || this.readonly) {
      return;
    }

    // Store value before search for potential restore
    if (this.value) {
      this.valueBeforeSearch = this.value;
    }

    // Trigger an async callback for contents and refresh data
    if (typeof this.state.beforeShow === 'function') {
      // user requested empty typeahead when initially opening the popup (IDS-7643)
      const data = await this.state.beforeShow({
        typeahead: ''
      });
      if (data) {
        this.loadDataSet(data);
        this.optionsData = data;

        if (this.#isMultiSelect) {
          const multiselect = this as unknown as IdsMultiselect;
          multiselect.updateSelected();
        }
      }
    } else {
      this.setOptionsData();
      this.setOptionsDataStored = true;
    }

    if (this.value) {
      this.dropdownList.value = this.value;
    }

    // Open the Dropdown List
    this.configurePopup();
    this.dropdownList.setAttribute(htmlAttributes.ARIA_EXPANDED, 'true');
    this.dropdownList.removeAttribute(attributes.TABINDEX);
    if (this.multiColumn) this.dropdownList.listBox?.classList.add('is-table');
    if ((this.getRootNode() as ShadowRoot).host?.tagName.includes('IDS-DATA-GRID')) this.dropdownList.listBox?.classList.add('is-datagrid');

    this.setAttribute(htmlAttributes.ARIA_EXPANDED, 'true');

    this.dropdownList.show();

    if (this.comboBox && !this.#isMultiSelect) {
      const inputText = this.input?.value?.trim() || '';

      // Sync stored IDs from dataset before using selectedOption getter
      this.#syncStoredOptionIds();

      // Re-sync the input text with the correct option label after restoring selectedOptionData
      if (this.value && this.selectedOptionData) {
        this.#syncComboTextWithOption(this.value);
      }

      // Use selectedOption getter which respects selectedOptionId for duplicate values
      const currentlySelected = this.selectedOption;

      if (currentlySelected && !currentlySelected.classList.contains('is-selected')) {
        this.selectOption(currentlySelected as IdsListBoxOption);
      } else if (!currentlySelected && inputText) {
        this.options.forEach((option) => {
          option.removeAttribute(htmlAttributes.ARIA_SELECTED);
          option.classList.remove('is-selected');
          option.setAttribute(attributes.TABINDEX, '-1');
        });
        this.dropdownList?.clearSelected();
      }
    }

    if (this.input) this.input.active = true;

    // Store initial state for Escape key restoration
    // MUST be AFTER combobox selectOption call to capture correct selectedIndex
    this.initialSelectedIndex = this.state.selectedIndex;

    if (this.autoSize && this.dropdownList?.listBox?.options) {
      this.setWidthBasedOnListBoxOptions(this.dropdownList?.listBox?.options);
    }

    // Focus and select input when typeahead is enabled
    if (this.typeahead || this.comboBox) {
      if (this.typeahead) this.#attachTypeaheadEvents();
      if (this.comboBox) this.#attachComboboxEvents();
      this.input?.removeAttribute(attributes.READONLY);
      this.input?.focus();
      setTimeout(() => {
        // push to end of callstack to ensure input will be focused
        this.searchField?.input?.focus();
      });
      this.#connectSearchField();
    }

    if (shouldSelect) {
      this.input?.input?.select();
    }

    this.container?.classList.add('is-open');
    this.attachOpenEvents();
    this.triggerOpenEvent();

    if (this.input?.input) {
      this.input.input.scrollLeft = 0;
    }
  }

  /**
   * Populate the DOM with the dataset
   * @param {IdsDropdownOptions} dataset The dataset to use with value, label ect...
   * @private
   */
  loadDataSet(dataset: IdsDropdownOptions) {
    let html = '';
    let listbox = this.dropdownList?.querySelector<IdsListBox>('ids-list-box') || this.querySelector<IdsListBox>('ids-list-box');
    const listboxOptions = listbox?.options;

    if (listboxOptions) {
      let noDifference = listboxOptions.length === dataset.length;
      if (noDifference) {
        noDifference = dataset.every((option: IdsDropdownOption, idx: number) => option.value === listboxOptions[idx].value);
      }
      if (noDifference) return;
    }

    if (listbox) listbox.innerHTML = '';

    if (!listbox) {
      listbox = document.createElement('ids-list-box') as IdsListBox;
      if (this.multiColumn && this.columnsData.length > 0) {
        listbox.classList.add('multi-column');
      }
      this.dropdownList?.insertAdjacentElement('afterbegin', listbox);
      this.dropdownList?.configureListBox();
      this.configureDropdownList();
    }

    dataset.forEach((option: IdsDropdownOption) => {
      if (this.multiColumn) {
        const optionText = this.#processMultiColumnValue('', option);
        html += this.#templateListBoxOption({ ...this.#sanitizeOption(option), label: optionText });
      } else {
        html += this.#templateListBoxOption(this.#sanitizeOption(option));
      }
    });
    listbox?.insertAdjacentHTML('afterbegin', html);
    listbox?.options?.forEach((option: any, index: number) => { option.rowIndex = index; });
    this.dropdownList?.configureBlank();
    this.dropdownList?.configureDescription();
    if (!this.#isMultiSelect) {
      const currentValue = this.getAttribute(attributes.VALUE);
      if (this.value !== currentValue) {
        this.value = currentValue;
      }
    }
  }

  /**
   * Set the dropdown to combobox feature
   * @param {string} value  The value of combobox
   */
  set noSearch(value: string) {
    this.setAttribute(attributes.NO_SEARCH, value);
  }

  /**
   * Get the no search state
   * @returns {boolean} true if search is disabled
   */
  get noSearch(): boolean {
    return stringToBool(this.getAttribute(attributes.NO_SEARCH));
  }

  /**
   * Set the dropdown to combobox feature
   * @param {string} value  The value of combobox
   */
  set comboBox(value: string) {
    this.setAttribute(attributes.COMBOBOX, value);
  }

  /**
   * Get the combobox state
   * @returns {boolean} true if combobox is enabled
   */
  get comboBox(): boolean {
    return stringToBool(this.getAttribute(attributes.COMBOBOX));
  }

  /**
   * An async function that fires when you call combosearch.
   * @param {Function} func The async function
   */
  set comboSearch(func) {
    this.state.comboSearch = func;
  }

  /**
   * Get the combo search function
   * @returns {Function} the combo search function
   */
  get comboSearch() {
    return this.state.comboSearch;
  }

  /**
   * An async function that fires as the dropdown is opening allowing you to set contents.
   * @param {Function} func The async function
   */
  set beforeShow(func) {
    this.state.beforeShow = func;
  }

  /**
   * Get the before show function
   * @returns {Function} the before show function
   */
  get beforeShow() { return this.state.beforeShow; }

  /**
   * Sets the dropdown options data programmatically
   * @param {IdsDropdownOptions} val Array of dropdown option objects to load
   */
  set data(val: IdsDropdownOptions) {
    if (Array.isArray(val)) {
      this.optionsData = val;
      this.loadDataSet(val);
    }
  }

  /**
   * Get the dropdown options data
   * @returns {IdsDropdownOptions} the options data
   */
  get data(): IdsDropdownOptions {
    return this.optionsData;
  }

  /**
   * Sets the dropdown columns configuration for multi-column layout
   * @param {IdsDropdownColumn[]} val Array of column configuration objects
   */
  set columns(val: IdsDropdownColumn[]) {
    if (Array.isArray(val)) {
      this.columnsData = val;
      if (this.multiColumn && this.optionsData.length > 0) {
        this.loadDataSet(this.optionsData);
      }
    }
  }

  /**
   * Get the dropdown columns configuration
   * @returns {IdsDropdownColumn[]} the columns configuration
   */
  get columns(): IdsDropdownColumn[] {
    return this.columnsData;
  }

  /**
   * Sets the maximum number of columns to display
   * @param {number} val Maximum number of columns (default: 10)
   */
  set maxColumns(val: number) {
    if (typeof val === 'number' && val > 0) {
      this.maxColumnsValue = Math.min(val, 20);
      this.setAttribute(attributes.MAX_COLUMNS, this.maxColumnsValue.toString());
      if (this.multiColumn && this.optionsData.length > 0) {
        this.loadDataSet(this.optionsData);
      }
    }
  }

  /**
   * Get the maximum number of columns
   * @returns {number} the maximum columns limit
   */
  get maxColumns(): number {
    return this.maxColumnsValue || 10;
  }

  /**
   * Transforms HTML structure with multi-key spans into readable text format
   * Extracts text content from span elements and joins them with ' - ' separator
   * @param {string} htmlStructure - HTML string containing span elements with 'multi-key' class
   * @returns {string} Formatted text with values separated by ' - ' (e.g., "EUR - Euros")
   */
  transformMultiKeySpans(htmlStructure: string) {
    if (!htmlStructure) return '';
    const matches = htmlStructure.match(/<span class="multi-key"[^>]*>([^<]+)<\/span>/g);
    const values = matches?.map((match) => match.replace(/<[^>]+>/g, '')) || [];
    return values.join(' - ');
  }

  /**
   * Close the dropdown popup
   * @param {boolean} noFocus if true do not focus on close
   */
  close(noFocus?: boolean) {
    this.removeOpenEvents();

    if (this.dropdownList) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      if (this.dropdownList?.popup?.visible) this.dropdownList.hide(!noFocus);
      if (this.input) {
        this.input.active = false;
        this.removeFieldContainerStyles();
      }
      this.dropdownList.setAttribute(attributes.TABINDEX, '-1');
      this.dropdownList.removeAttribute(htmlAttributes.ARIA_EXPANDED);
    }

    this.setAttribute(htmlAttributes.ARIA_EXPANDED, 'false');

    if (!noFocus) {
      this.input?.focus();
    }
    if (this.typeahead) {
      this.loadDataSet(this.optionsData);
      if (!this.comboBox && !this.hasSelected) {
        this.input?.setAttribute(attributes.READONLY, 'true');

        // If value is empty and we have a stored value, restore it if it still exists
        if (!this.value && this.valueBeforeSearch) {
          const optionExists = this.optionsData?.some((opt) => opt.value === this.valueBeforeSearch);
          if (optionExists) {
            this.value = this.valueBeforeSearch;
            this.noResultsSelected = true;
            this.selectTooltip(null);
          }
        }

        let initialValue;
        if (this.hasAvatar) {
          const spanText = this.selectedOption?.querySelector('span')?.textContent?.replace(/\s+/g, ' ').trim() || '';
          initialValue = this.showValue ? this.value ?? '' : spanText;
        } else {
          initialValue = this.showValue ? this.value ?? '' : this.selectedOption?.textContent?.trim() || '';
        }
        if (this.input) {
          this.input.value = initialValue || '';
          this.selectIcon(this.selectedOption);
        }
      }
      (window.getSelection() as Selection).removeAllRanges();
      this.replaceTriggerIcon(this.dropdownIcon || 'dropdown');
      this.typeaheadInput = null;
      this.valueBeforeSearch = null; // Clear stored value
    }

    // Handles the multicolumn using regex spans instead of indexes to properly handle text division such as /n and spaces
    if (this.multiColumn) {
      const multiValue = this.transformMultiKeySpans(this.selectedOption?.innerHTML || '');

      if (this.input) this.input.value = this.showValue ? (this.value ?? null) : multiValue || '';
      if (this.comboBox && this.input && multiValue) this.input.value = this.value ?? null;

      /**
       * Set input value based on display preferences and multicolumn data
       * Uses showValue flag to determine whether to display raw value or formatted multiValue
       * Special handling for comboBox mode when no multiValue is available
       */
      if (this.input) {
        this.input.value = this.showValue ? (this.value ?? null) : (multiValue || '');

        // Override for comboBox without multiValue
        if (this.comboBox && !multiValue) {
          this.input.value = this.value ?? null;
        }

        // Reset scroll position to show the beginning of the text
        if (this.input?.input) {
          this.input.input.scrollLeft = 0;
        }
      }
    }

    this.container?.classList.remove('is-open');

    if (!this.noResultsSelected) {
      this.selectTooltip(this.selectedOption);
    }
    this.noResultsSelected = false;

    this.triggerCloseEvent(noFocus);
  }

  /**
   * Remove dropdown's input field container parent style
   * @returns {void}
   */
  removeFieldContainerStyles() {
    if (this.autoSize && this.input?.container?.children) {
      const fieldContainer = this.getInputFieldContainer();
      if (fieldContainer) {
        fieldContainer.style.removeProperty('width');
        fieldContainer.style.removeProperty('max-width');
      }
    }
  }

  /**
   * Get dropdown's input field container parent
   * @returns {void}
   */
  getInputFieldContainer() {
    if (!this.input?.container) return;
    return this.input?.container?.querySelector('.field-container') as HTMLElement;
  }

  /**
   * Calculate and set the input width based on the longest text in listbox options
   * @param {HTMLElement[]} options Array of listbox option elements
   * @returns {void}
   */
  setWidthBasedOnListBoxOptions(options: HTMLElement[]): void {
    if (!options || !options.length) return;

    // Get all option texts
    const optionTexts = options.map((option) => option.textContent || (option as any).label || '');
    const isMultiSelect = options[0].classList.contains('multiselect-option');

    // Find the longest text
    const longestText = this.getLongestText(optionTexts);

    // Set the width based on the longest text
    if (this.inputAutoSize) {
      this.setInputWidthForText(longestText, isMultiSelect);
    } else if (this.autoSize && !this.inputAutoSize) {
      this.setListboxOptionWidthToFieldContainer();
    }
  }

  /**
   * Find the longest text from an array of strings
   * @param {string[]} texts Array of text strings
   * @returns {string} The longest text
   */
  getLongestText(texts: string[]): string {
    if (!texts || !texts.length) return '';

    return texts.reduce((longest, current) => (current.length > longest.length ? current : longest), '');
  }

  /**
   * Set the listbox, listbox option and span width to match the field container width
   * @returns {void}
   */
  setListboxOptionWidthToFieldContainer(): void {
    if (!this.input?.container || !this.dropdownList?.listBox) return;

    const fieldContainer = this.input.container.querySelector('.field-container') as HTMLElement;
    if (!fieldContainer) return;

    const offset = this.multiColumn ? 0 : 8;
    const fieldContainerWidth = (fieldContainer.clientWidth ?? fieldContainer.getBoundingClientRect().width) - offset;
    const scrollbarWidth = this.dropdownList.listBox.offsetWidth - this.dropdownList.listBox.clientWidth || 0;

    this.dropdownList.listBox.style.minWidth = `${fieldContainerWidth}px`;

    // Set minWidth on listbox options
    this.dropdownList.listBox?.querySelectorAll('ids-list-box-option').forEach((option) => {
      (option as HTMLElement).style.minWidth = `${fieldContainerWidth - scrollbarWidth}px`;

      const spans = option.querySelectorAll<HTMLElement>('span');
      if (spans.length && this.multiColumn) {
        let totalSpanWidth = 0;
        spans.forEach((span) => {
          totalSpanWidth += span.getBoundingClientRect().width;
        });
        const offsetWidth = (fieldContainerWidth - scrollbarWidth) - totalSpanWidth;
        const additionalWidth = offsetWidth / spans.length;
        spans.forEach((span) => {
          const currentWidth = span.getBoundingClientRect().width;
          span.style.minWidth = `${currentWidth + additionalWidth}px`;
        });
      }
    });
  }

  /**
   * Set the input width based on a specific text
   * @param {string} text The text to measure for width
   * @param {boolean} isMultiSelect If option is multiselect
   * @returns {void}
   */
  setInputWidthForText(text: string, isMultiSelect: boolean): void {
    if (!text || !this.input || !this.input.container) return;

    if (this.autoSize === true) {
      const fieldContainer = this.input.container.querySelector('.field-container') as HTMLElement;
      if (!fieldContainer) return;

      const currentWidth = fieldContainer.getBoundingClientRect().width;

      // Create a temporary span for measuring text width
      const span = document.createElement('span');
      span.style.visibility = 'hidden';
      span.style.position = 'absolute';
      span.style.whiteSpace = 'nowrap';

      // Copy font styles from the input to ensure accurate measurement
      const computedStyle = window.getComputedStyle(this.input.input || this.input.container);
      span.style.fontFamily = computedStyle.fontFamily;
      span.style.fontSize = computedStyle.fontSize;
      span.style.fontWeight = computedStyle.fontWeight;
      span.style.letterSpacing = computedStyle.letterSpacing;

      // Set the text content
      span.textContent = text;

      // Append to DOM temporarily for measurement
      document.body.appendChild(span);

      // Measure the width
      const textWidth = span.getBoundingClientRect().width;

      // Clean up
      document.body.removeChild(span);

      // Find the field container
      const paddingRight = isMultiSelect ? 50 : 8; // For trigger button or other elements
      const paddingLeft = 8; // For left padding
      const totalPadding = paddingLeft + paddingRight;

      const calculatedWidth = Math.ceil(textWidth) + totalPadding;
      const viewportWidth = window.visualViewport?.width || window.innerWidth;
      const fieldContainerRect = fieldContainer.getBoundingClientRect();
      const leftPosition = fieldContainerRect.left;
      const maxAllowedWidth = viewportWidth - leftPosition - POPUP_EDGE_OFFSET; // Consider left position and leave margin

      // Only expand, never shrink - use the larger of current width or calculated width, but cap at available space
      const finalWidth = Math.min(Math.max(calculatedWidth, currentWidth), maxAllowedWidth);
      fieldContainer.style.width = `${finalWidth}px`;
    }
  }

  /**
   * Toggle the dropdown list open/closed state
   * @param {boolean} shouldSelect whether or not the input text should be selected
   * @private
   */
  toggle(shouldSelect = false): void {
    if (!this.dropdownList) return;
    if (!this.dropdownList.popup?.visible) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.open(shouldSelect);
    } else {
      this.close();
    }
  }

  /**
   * Establish Internal Event Handlers
   * @private
   * @returns {object} The object for chaining.
   */
  #attachEventHandlers() {
    this.attachClickEvent();

    // Should not open if clicked on label
    this.offEvent('click.dropdown-label');
    this.onEvent('click.dropdown-label', this.labelEl, (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      this.labelClicked = true;

      // If dropdown is open, close it; otherwise just focus the input
      if (this.dropdownList?.popup?.visible) {
        this.close();
      } else {
        this.input?.focus();
      }

      // Reset scroll position to show the beginning of the text
      if (this.input?.input) {
        this.input.input.scrollLeft = 0;
      }
    });

    // Dropdown List's `hide` event can cause the field to become focused
    this.offEvent('hide.dropdown-list');
    this.onEvent('hide.dropdown-list', this.container, () => {
      if (this.dropdownList?.popup?.visible) this.close();
    });

    // Handle the Locale Change
    this.offEvent('languagechange.dropdown-container');
    this.onEvent('languagechange.dropdown-container', this.closest('ids-container'), () => {
      this.#addAria();
    });

    const slot = this.container?.querySelector('slot');
    this.offEvent('slotchange.dropdown', slot);
    this.onEvent('slotchange.dropdown', slot, () => {
      this.configureDropdownList();
      if (this.comboBox) {
        if (this.value) this.#syncComboTextWithOption(this.value);
        else this.#isFirstRender = false;
      } else if (this.value && !Array.isArray(this.value)) {
        this.#syncInputTextWithOption(this.value);
      }
      this.isInitializing = false;
    });

    if (this.comboBox) {
      this.onEvent('combosearch', this.input, async (e: KeyboardEvent) => {
        if (typeof this.state.comboSearch === 'function') {
          const data = await this.state.comboSearch({
            value: this.input?.value || (e.target as HTMLInputElement).value,
          });

          if (data) {
            this.loadDataSet(data);

            // Re-match current value against newly loaded options using partial matching
            if (this.value) {
              const matches = this.#findMatches(this.value);
              if (matches && matches.length > 0) {
                // Get the first match (best match)
                const firstMatch = matches[0];
                // Find the option element to get its rowIndex
                const matchingOption = this.options.find(
                  (opt) => opt.getAttribute('value') === firstMatch.value
                );
                if (matchingOption) {
                  this.state.selectedIndex = matchingOption.rowIndex;
                }
              } else {
                this.state.selectedIndex = -1;
              }
            }

            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this.open(this.comboBox);
            this.triggerEvent('input.dropdown', this.input?.input);
          }
        } else {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          this.open(this.comboBox);
        }
      });

      this.onEvent('combochange', this.input, async (e: KeyboardEvent) => {
        this.value = (e.detail as any).value;
        this.#syncComboboxTooltip(this.value);
      });
    }

    this.attachKeyboardOpenEvent();

    // Handle clearable button click
    if (this.clearable) {
      this.offEvent('cleared.dropdown');
      this.onEvent('cleared.dropdown', this.input, () => {
        this.isClearing = true;
        this.value = '';
        this.dropdownIconEl?.remove();
        this.dropdownColorSwatch?.remove();
        this.selectTooltip(null);
        this.selectedOptionData = null;
        requestAnimationFrame(() => {
          this.isClearing = false;
        });
      });
    }

    return this;
  }

  /**
   * Attach a keyboard event for Enter/Spacebar that opens the dropdown.
   * This needs to happen separately from the other event handlers because this
   * one is rebound every time the list is closed.
   */
  private attachKeyboardOpenEvent() {
    this.listen([' ', 'Enter'], this, (e: KeyboardEvent) => {
      if (this.readonly) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (this.closedByKeyboard) {
        this.typeaheadInput = null;
        this.closedByKeyboard = false;
        return;
      }

      const dropdownListVisible = !!this.dropdownList?.popup?.visible;
      if (dropdownListVisible) return;
      if (e.key === 'Enter' && !dropdownListVisible) return;
      if (e.key === ' ' && this.typeahead) return;

      this.openedByKeyboard = true;
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.open(this.typeahead);
    });
  }

  /**
   * Handle language changes by updating ARIA attributes
   */
  onLanguageChange = () => {
    this.#addAria();
  };

  /**
   * Connects event handlers related to activation of the Dropdown List
   */
  attachClickEvent() {
    if (!this.list) {
      this.offEvent('click.dropdown-input');
      this.onEvent('click.dropdown-input', this.input, (e) => {
        if (e instanceof MouseEvent) {
          const target = e.target as HTMLElement ?? null;
          const isClearButton = target?.classList.contains('btn-clear') || target?.closest('.btn-clear');
          if (isClearButton || this.isClearing) return;

          if (this.comboBox) {
            if (target?.tagName === 'IDS-TRIGGER-BUTTON') {
              e.preventDefault();

              if (this.dropdownList?.popup?.visible) {
                this.close(true);
                return;
              }

              if ((this.getRootNode() as any).host?.tagName.includes('IDS-DATA-GRID')) {
                const isOpen = target.parentElement?.parentElement?.classList.contains('is-open');
                if (isOpen) this.close(true);
              }

              this.triggerEvent('combosearch', this.input, { detail: { value: this.input?.value || '' }, bubbles: true });
            }
          } else {
            this.dropdownList?.onTriggerClick?.();
          }
        }
      });
    }

    // Respond to open/close events from an external IdsDropdownList component
    if (this.dropdownList) {
      this.offEvent('open.dropdown-list');
      this.offEvent('close.dropdown-list');
      this.onEvent('open.dropdown-list', this.dropdownList, async (e: CustomEvent) => {
        e.stopPropagation();
        await this.open();
      });
      this.onEvent('close.dropdown-list', this.dropdownList, (e: CustomEvent) => {
        e.stopPropagation();
        this.typeaheadInput = null;
        this.close(e.detail.operation === 'cancel');
      });
    }

    if (this.input?.fieldContainer) {
      this.offEvent('selected.dropdown-list');
      this.onEvent('selected.dropdown-list', this.input.fieldContainer, (e: CustomEvent) => {
        this.hasSelected = true;
        e.stopPropagation();
        if (e.detail.selectedElem) {
          this.selectedOptionData = {
            id: e.detail.selectedElem.getAttribute('id'),
            label: e.detail.selectedElem.textContent?.trim() || null
          };

          const cell = this.closest('ids-data-grid-cell') as IdsDataGridCell;
          if (cell && this.selectedOptionData) {
            if (this.selectedOptionData.id) cell.dataset.selectedOptionId = this.selectedOptionData.id;
            if (this.selectedOptionData.label) cell.dataset.selectedOptionLabel = this.selectedOptionData.label;
          }
        }
        this.value = e.detail.value;
        this.#syncInputTextWithOption(this.value);
        this.triggerSelectedEvent(e);

        // add timeout to move the close to end of callstack as selected event listeners
        // needed the listbox state (e.g. searching) when the user perform the selection
        setTimeout(() => {
          this.typeaheadInput = null;
          if (this.dropdownList?.popup?.visible) this.close();
          this.hasSelected = false;
        });
      });
    }

    this.offEvent('change.list', this.input);
    this.onEvent('change.list', this.input, () => {
      if (this.dropdownList?.popup?.visible && !this.typeahead && !this.comboBox) this.close();
    });
  }

  /**
   * Connects interaction events that should only be present when the
   * dropdown list is open
   */
  private attachOpenEvents() {
    this.unlisten(' ');
    this.unlisten('Enter');
    this.attachKeyboardSelectionEvent();

    // Select on Tab (only for non-multiselect)
    if (!this.#isMultiSelect) {
      this.listen(['Tab'], this, (e: KeyboardEvent) => {
        if (!this.dropdownList?.popup?.visible) {
          return;
        }

        // For combobox mode, check if input text matches a valid option
        if (this.comboBox) {
          const inputText = this.input?.value?.trim() || '';
          const matchingOption = this.#findMatchingOption(inputText);

          if (matchingOption) {
            this.value = matchingOption.getAttribute(attributes.VALUE) || '';
            this.#syncInputTextWithOption(this.value);
            this.triggerSelectedEvent();
          }
          this.close(true);
          return;
        }

        e.preventDefault();
        e.stopPropagation();

        // Check for highlighted option that's different from current value
        const highlightedOption = this.options.find((opt) => opt.classList.contains('is-selected'));
        if (highlightedOption) {
          const optionValue = highlightedOption.getAttribute(attributes.VALUE) || '';
          if (optionValue !== this.value) {
            this.selectOption(highlightedOption);
          }
        }

        // Close dropdown and move focus
        this.close(true);
        this.moveFocus(!e.shiftKey);
      });
    }
  }

  /**
   * Establish selection event for keyboard interactions.
   * Overrides a similiar method from IdsDropdown for Multiselect-specific behavior.
   */
  attachKeyboardSelectionEvent() {
    if (!this.#isMultiSelect) {
      // Select or Open on space/enter
      this.listen(['Enter'], this, (e: Event) => {
        if (!this.dropdownList?.popup?.visible) return;
        if (this.openedByKeyboard) {
          this.openedByKeyboard = false;
          return;
        }

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        // For combobox mode, validate input text before selecting
        if (this.comboBox) {
          const inputText = this.input?.value?.trim() || '';
          const matchingOption = this.#findMatchingOption(inputText);

          const optionToSelect = (matchingOption && !this.selected) ? matchingOption : this.selected;

          if (optionToSelect) {
            const value = optionToSelect.getAttribute(attributes.VALUE) || '';
            this.value = value;
            this.#syncInputTextWithOption(this.value);
            this.triggerSelectedEvent();
          }

          this.closedByKeyboard = true;
          this.close();
        } else {
          // Non-combobox behavior - select highlighted or first option
          if (!this.selected) this.#selectFirstOption();
          const value = this.selected?.getAttribute(attributes.VALUE) || '';

          this.value = value;
          this.#syncInputTextWithOption(this.value);
          this.closedByKeyboard = true;
          this.close();
          this.triggerSelectedEvent();
        }
      });
    }
  }

  private removeOpenEvents() {
    this.unlisten(' ');
    this.unlisten('Enter');
    this.unlisten('Tab');

    this.openedByKeyboard = false;
    this.attachKeyboardOpenEvent();
  }

  /**
   * Establish Internal Keyboard shortcuts
   * @private
   * @returns {object} This API object for chaining
   */
  #attachKeyboardListeners() {
    this.onEvent('keydown.dropdown-typeahead', this, async (e: KeyboardEvent) => {
      if (this.readonly || this.comboBox) return;

      const key = e.key || 'Space';
      if (['Backspace', 'Delete', 'Enter', 'Escape', 'Shift', 'Tab'].includes(key)) return;

      if (!this.dropdownList?.popup?.visible) {
        const doNotOpen = ['Alt', 'Backspace', 'CapsLock', 'Control', 'Delete', 'Enter', 'Escape', 'Meta', 'Shift', 'Tab'];
        if (doNotOpen.includes(key)) return;

        await this.open(true);
        if (this.typeahead && typeof this.state.beforeShow === 'function') {
          if (!['ArrowLeft', 'ArrowRight'].includes(e.key)) {
            this.input!.input!.value = e.key;
            this.#typeAhead(e.key);
          }
          e.preventDefault();
        }
      }
    });

    // Handle up and down arrow
    this.listen(['ArrowDown', 'ArrowUp'], this, (e: KeyboardEvent) => {
      if (this.readonly) return;
      e.stopPropagation();
      e.stopImmediatePropagation();
      e.preventDefault();

      // If dropdown isn't open yet, open it
      if (!this.dropdownList?.popup?.visible) {
        if (this.comboBox && e.key === 'ArrowDown') {
          this.triggerEvent('combosearch', this.input, { bubbles: true });
        } else {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          this.open(this.typeahead);
        }
        return;
      }

      // Handle Alt+Up to close dropdown
      if (e.key === 'ArrowUp' && e.altKey) {
        if (!this.#isMultiSelect) {
          this.value = this.selected?.getAttribute(attributes.VALUE) || '';
        }
        this.close();
        return;
      }

      // Get current selection state and footer buttons
      const selected: any = this.selected;
      const footerButtons = this.dropdownList?.container?.querySelectorAll('.multiselect-btn') || [];
      const currentFocusedButton = this.dropdownList?.container?.querySelector('.multiselect-btn:focus');

      // Handle navigation from footer buttons
      if (currentFocusedButton) {
        if (e.key === 'ArrowDown') {
          // Find next button or stay on last button
          const buttons = Array.from(footerButtons);
          const currentIndex = buttons.indexOf(currentFocusedButton);
          if (currentIndex < buttons.length - 1) {
            (buttons[currentIndex + 1] as HTMLElement).focus();
          }
          // Stay on the last button when pressing down
        } else if (e.key === 'ArrowUp') {
          // Find previous button or go to last option
          const buttons = Array.from(footerButtons);
          const currentIndex = buttons.indexOf(currentFocusedButton);
          if (currentIndex > 0) {
            (buttons[currentIndex - 1] as HTMLElement).focus();
          } else {
            // Go to last option
            const lastOption = this.getLastSelectableOption();
            if (lastOption) {
              this.deselectOption(selected);
              this.selectOption(lastOption);
            }
          }
        }
        return;
      }

      // If no selection exists, select first option
      if (!selected && e.key === 'ArrowDown') {
        this.#selectFirstOption();
        return;
      }

      // Find next/previous elements, accounting for group labels, description options, and separators
      let targetElement = null;

      if (e.key === 'ArrowDown') {
        let next = selected?.nextElementSibling;
        while (next && (next.hasAttribute(attributes.GROUP_LABEL) || next.hasAttribute(attributes.DESCRIPTION) || next.tagName === 'IDS-SEPARATOR')) {
          next = next.nextElementSibling;
        }

        if (!next) {
          // At the end of options, move to footer buttons if they exist
          if (footerButtons.length > 0) {
            this.deselectOption(selected);
            (footerButtons[0] as HTMLElement).focus();
            return;
          }
          return; // At the end of the list
        }

        targetElement = next;
      } else if (e.key === 'ArrowUp') {
        let prev = selected?.previousElementSibling;
        while (prev && (prev.hasAttribute(attributes.GROUP_LABEL) || prev.hasAttribute(attributes.DESCRIPTION) || prev.tagName === 'IDS-SEPARATOR')) {
          prev = prev.previousElementSibling;
        }

        if (!prev) return; // At the beginning of the list

        targetElement = prev;
      }

      // Only proceed if we found a valid target element
      if (targetElement) {
        // Deselect current option and select the new one
        this.deselectOption(selected);
        this.selectOption(targetElement);

        // Ensure the newly selected item is visible in the dropdown
        if (targetElement.scrollIntoView) {
          targetElement.scrollIntoView({ block: 'nearest' });
        }
      }
    });

    // Handle left and right arrow for footer buttons only
    this.listen(['ArrowLeft', 'ArrowRight'], this, (e: KeyboardEvent) => {
      if (!this.dropdownList?.popup?.visible) return;

      const footerButtons = this.dropdownList?.container?.querySelectorAll('.multiselect-btn') || [];
      const currentFocusedButton = this.dropdownList?.container?.querySelector('.multiselect-btn:focus');

      // Only handle left/right navigation if we're on a footer button
      if (currentFocusedButton && footerButtons.length > 0) {
        e.stopPropagation();
        e.stopImmediatePropagation();
        e.preventDefault();

        const buttons = Array.from(footerButtons);
        const currentIndex = buttons.indexOf(currentFocusedButton);

        if (e.key === 'ArrowRight' && currentIndex < buttons.length - 1) {
          (buttons[currentIndex + 1] as HTMLElement).focus();
        } else if (e.key === 'ArrowLeft' && currentIndex > 0) {
          (buttons[currentIndex - 1] as HTMLElement).focus();
        }
      }
    });

    // Close on escape
    this.listen(['Escape'], this, () => {
      // Restore original selectedIndex before closing
      this.isCancelingSelection = true;
      if (this.initialSelectedIndex !== undefined && this.initialSelectedIndex !== null) {
        this.state.selectedIndex = this.initialSelectedIndex;
      }
      this.close();
      this.isCancelingSelection = false;
      this.initialSelectedIndex = 0; // Reset after use
    });

    // Handle Tab/Shift+Tab
    this.listen(['Tab'], this, (e: KeyboardEvent) => {
      if (!this.dropdownList?.popup?.visible) return;

      // Tab/Shift+Tab exits the dropdown and moves focus
      e.preventDefault();
      e.stopPropagation();
      this.close(true);
      this.moveFocus(!e.shiftKey);
    });

    // Handle Space for footer buttons
    this.listen([' '], this, (e: KeyboardEvent) => {
      if (!this.dropdownList?.popup?.visible) return;

      const focusedButton = this.dropdownList?.container?.querySelector<IdsButton>('.multiselect-btn:focus');

      if (focusedButton) {
        // Space activates the focused button
        e.preventDefault();
        focusedButton.click();
      }
    });

    // Delete/backspace should clear the value
    this.listen(['Backspace', 'Delete'], this, () => {
      if (!this.clearable || (this.clearable && this.typeahead && this.dropdownList?.popup?.visible)
        || this.readonly) return;

      if (this.allowBlank) {
        this.value = 'blank';
      } else {
        // ids-multiselect shared
        (this.value as any) = this.#isMultiSelect ? [] : '';
        if (this.input) this.input.value = '';
        this.selectIcon(null);
        this.selectTooltip(null);
        this.selectedOptionData = null;
      }

      this.input?.focus();
    });

    return this;
  }

  #createListBoxSelector(value: string | null | undefined) {
    let selector = this.generateSelectorForListBoxOption(value || '');
    if (value === ' ' || !value) {
      selector = `ids-list-box-option[value=""]:not([group-label]), ids-list-box-option:not([value]):not([group-label])`;
    }

    const matchingOptions = [...this.dropdownList?.listBox?.querySelectorAll<IdsListBoxOption>(selector) ?? []];

    if (matchingOptions.length > 1 && (this.selectedOptionData?.id || this.selectedOptionData?.label)) {
      const cell = this.closest('ids-data-grid-cell') as IdsDataGridCell;
      const storedId = this.selectedOptionData?.id || cell?.dataset.selectedOptionId;
      const storedLabel = this.selectedOptionData?.label || cell?.dataset.selectedOptionLabel;

      const match = matchingOptions.find((opt) => opt.getAttribute('id') === storedId || opt.textContent?.trim() === storedLabel);
      if (match) return match;
    }

    return matchingOptions.at(0);
  }

  /**
   * Flag to track initial render state for combobox value synchronization
   * @private
   */
  #isFirstRender = true;

  /**
   * Syncs selected option combo text with dropdown input
   * @param {string|null|undefined} value option value
   */
  #syncComboTextWithOption(value: string | null | undefined) {
    if (!this.comboBox) return;
    // COMBO BOX IMPLEMENTATION
    const setValue = (val: string, index?: number) => {
      if (this.input?.input) {
        this.input.value = val;
        this.input.input.value = val;

        // Only set to -1 if no comboSearch (static options)
        // If comboSearch exists, keep current selectedIndex until dynamic load completes
        if (index !== undefined) {
          this.state.selectedIndex = index;
        } else if (!this.comboSearch && !this.isCancelingSelection) {
          this.state.selectedIndex = -1;
        }
        // else: keep current selectedIndex, will be updated after loadDataSet
      }
      this.#syncComboboxTooltip(val);
    };

    const matchingOptionIndex = this.#findMatchingOption(value || '')?.rowIndex;

    if (this.#isFirstRender) {
      requestAnimationFrame(() => {
        const defaultOption = this.#createListBoxSelector(value);
        if (defaultOption?.value && defaultOption?.textContent) {
          const displayValue = this.showValue ? defaultOption.value : defaultOption.textContent.trim();
          setValue(displayValue, matchingOptionIndex);
        }
        this.#isFirstRender = false;
      });
    } else {
      if (!this.showValue && value && this.input?.value !== value) {
        const option = this.#createListBoxSelector(value);
        if (option?.textContent) {
          setValue(option.textContent.trim(), matchingOptionIndex);
          return;
        }
      }
      setValue(value ?? '', matchingOptionIndex);
    }
  }

  /**
   * Syncs selected option text with dropdown input
   * @param {string|null|undefined} value option value
   */
  #syncInputTextWithOption(value: string | null | undefined) {
    // DROPDOWN IMPLEMENTATION
    const listBoxOption = this.#createListBoxSelector(value);
    if (!listBoxOption) {
      return;
    }

    // NOTE: setAttribute() must be called here, before the internal input.value is set below
    this.setAttribute(attributes.VALUE, String(value));
    this.clearSelected();

    // Allows the combobox to still accept no results as a blank or no value when in typeahead
    if (this.value === '' && this.comboBox && this.typeahead) return;

    this.selectOption(listBoxOption);
    this.selectIcon(listBoxOption);
    this.selectTooltip(listBoxOption);
    if (this.input?.input) {
      let textContent;
      // For module nav with avatar, use only span text, not full textContent
      if (this.closest('ids-module-nav') && this.hasAvatar) {
        const spanText = listBoxOption.querySelector('span')?.textContent?.replace(/\s+/g, ' ').trim() || '';
        textContent = this.showValue ? this.value ?? '' : spanText;
      } else {
        textContent = this.showValue ? this.value ?? '' : listBoxOption.textContent?.trim() ?? '';
      }
      if (this.hasSelected && this.typeaheadInput === textContent) {
        this.input.value = '';
        this.typeaheadInput = null;
      }

      this.input.value = textContent;
      if (this.input?.input) this.input.input.value = textContent;
    }
    this.state.selectedIndex = listBoxOption.rowIndex
      ?? [...((listBoxOption?.parentElement as any)?.children || [])].indexOf(listBoxOption);
  }

  /**
   * Sets the value of tooltip for combobox
   * @param {string|null|undefined} value value of tooltip
   */
  #syncComboboxTooltip(value: string | null | undefined) {
    this.tooltip = value ?? '';
    if (value) {
      this.setAttribute(attributes.TOOLTIP, value);
    } else {
      this.removeAttribute(attributes.TOOLTIP);
    }

    if (this.tooltipEl) this.tooltipEl.target = this.input?.input;
  }

  /**
   * Process html value for multi column property
   * @param {string} text the filter regex
   * @param {IdsDropdownOption} item the dropdown option
   * @returns {string} formatted value for multi column
   */
  #processMultiColumnValue(text: string, item: IdsDropdownOption) {
    const regex = new RegExp(text, 'gi');
    const highlightMatch = (str: string) => str.replace(regex, (matched) => (matched ? `<span class="highlight">${matched}</span>` : ''));

    if (this.columnsData.length > 0) {
      const limitedColumns = this.columnsData.slice(0, this.maxColumns);
      let optionText = '';

      limitedColumns.forEach((column) => {
        const cellValue = item[column.id] || '';
        const tempText = highlightMatch(cellValue.toString());
        optionText += `<span class="multi-key">${tempText}</span>`;
      });

      return optionText;
    }

    let optionText = '';
    let itemLabels: any[] = [];
    if (item.label.indexOf('\n') > -1) {
      itemLabels = item.label.replace(/ +(?= )/g, '').split('\n');
    } else {
      itemLabels.push(item.label);
    }

    let tempText = '';
    itemLabels.forEach((object: string) => {
      tempText = object.replace(
        regex,
        (matched) => {
          if (matched) {
            return `<span class="highlight">${matched}</span>`;
          }
          return '';
        }
      );
      optionText += `<span class="multi-key">${tempText}</span>`;
    });

    return optionText;
  }

  /**
   * Handle typeahead functionality
   * @param {string} text keydownend event detail keys
   * @returns {void}
   */
  #typeAhead(text: string) {
    const resultsArr = this.#findMatches(text).filter((item: IdsDropdownOption) => item.value !== 'select-all');
    const firstSelectedElement = this.dropdownList?.listBox?.shadowRoot?.querySelector('.first-selected');

    let selectAllOption = '';
    if (this.#isMultiSelect && this.selectAll) {
      selectAllOption = `<ids-list-box-option value="select-all" class="multiselect-option select-all-option">
        <ids-checkbox no-margin class="justify-center multiselect-checkbox ellipsis" label="Select all"></ids-checkbox>
      </ids-list-box-option>`;
    }

    // when typing ahead, remove the `selected` label in the list
    if (firstSelectedElement) {
      firstSelectedElement.remove();
    }

    const results = resultsArr.map((item: IdsDropdownOption) => {
      const regex = new RegExp(text, 'gi');
      let optionText = '';

      if (this.multiColumn && !item.groupLabel) {
        optionText = this.#processMultiColumnValue(text, item);
      } else {
        optionText = item.groupLabel ? item.label : item.label?.replace(
          regex,
          (matched) => {
            if (matched) {
              return `<span class="highlight">${matched}</span>`;
            }
            return '';
          }
        );
      }

      return this.#templateListBoxOption({
        ...item,
        originalLabel: item.label,
        label: optionText
      });
    }).join('');

    if (this.dropdownList) {
      if (this.dropdownList.listBox) {
        if (results) {
          this.dropdownList.listBox.innerHTML = selectAllOption + results;
        } else {
          this.dropdownList.listBox.innerHTML = `${selectAllOption}<ids-list-box-option value="" ${attributes.NO_RESULTS}>${this.showNoResults ? this.localeAPI.translate('NoResults') : ''}</ids-list-box-option>`;
        }
        this.dropdownList.listBox.options?.forEach((option: any, index: number) => { option.rowIndex = index; });
      }

      // Change location of the popup after results are populated and the popup's height change
      this.dropdownList.popup?.place();
    }

    this.replaceTriggerIcon('search');

    // Remove selected input icon when start typing
    this.input?.querySelector('.trigger-icon')?.remove();
    if (this.#isMultiSelect && this.selectAll) this.updateSelectAllState();
    this.typeaheadInput = this.input?.value || '';
  }

  /**
   * Find and select the only option by input text provided
   * @param {string} input the text to find by
   */
  #selectMatch(input: string) {
    const term: string = escapeRegExp(input)?.trim();

    if (!term) return;

    const option = this.options
      .filter((item: IdsListBoxOption) => !item.hasAttribute(attributes.GROUP_LABEL)
        && !item.hasAttribute(attributes.DESCRIPTION))
      .find((item: IdsListBoxOption) => {
        const regex = new RegExp(`^(${term})`, 'i');
        const label = this.#isMultiSelect ? item.querySelector<IdsCheckbox>('ids-checkbox')?.label : item.textContent?.trim();

        return label?.match(regex);
      });

    if (this.dropdownList?.popup?.visible && option) {
      this.deselectOption(this.selected);
      this.selectOption(option as IdsListBoxOption);

      return;
    }

    const value = option?.getAttribute(attributes.VALUE);

    if (value) {
      if (this.#isMultiSelect) {
        const filtered: string[] = (this.value as any).filter((item: string) => item !== value);

        (this.value as any) = [...filtered, value];
      } else {
        this.value = value;
      }
    }
  }

  /**
   * Helper to replace icon on the trigger button
   * @param {string} icon ids-icon icon value
   */
  replaceTriggerIcon(icon: string) {
    const triggerIcon = this.container?.querySelector<IdsTriggerButton>('ids-trigger-button')?.querySelector<IdsIcon>('ids-icon');

    if (triggerIcon?.icon && triggerIcon.icon !== icon) {
      triggerIcon.icon = icon;
    }
  }

  /**
   * Select first no blank with value option
   */
  #selectFirstOption() {
    this.clearSelected();

    if (this.options.length > 0) {
      const firstWithValue = [...this.options].filter((item) => {
        const value = item.getAttribute(attributes.VALUE);
        const groupLabel = item.hasAttribute(attributes.GROUP_LABEL);
        const isDescription = item.hasAttribute(attributes.DESCRIPTION);

        return value && value !== 'blank' && !groupLabel && !isDescription;
      })[0];

      this.selectOption(firstWithValue);
    }
  }

  /**
   * Get the last selectable option
   * @private
   * @returns {IdsListBoxOption | null} The last selectable option
   */
  private getLastSelectableOption(): IdsListBoxOption | null {
    const selectableOptions = [...this.options].filter((item) => {
      const value = item.getAttribute(attributes.VALUE);
      const groupLabel = item.hasAttribute(attributes.GROUP_LABEL);
      const isDescription = item.hasAttribute(attributes.DESCRIPTION);

      return value && value !== 'blank' && !groupLabel && !isDescription;
    });

    return selectableOptions[selectableOptions.length - 1] || null;
  }

  /**
   * Create the list box option template.
   * @param {IdsDropdownOption} option data object
   * @returns {string} ids-list-box-option template
   */
  #templateListBoxOption(option: IdsDropdownOption): string {
    const originalLabelTooltip = `${option.originalLabel ? `tooltip="${option.originalLabel}` : 'tooltip="true"'}"`;
    const tooltipAttr = option.tooltip ? `${attributes.TOOLTIP}="${option.tooltip}"` : '';
    const label = option.avatar ? `<span>${option.label || ''}</span>` : `${(option.label || '')}`;
    const content = [
      option.avatar ? option.avatar : '',
      option.icon ? `<ids-icon icon="${option.icon}"${option.iconColor ? ` color="${option.iconColor}"` : ''}></ids-icon>` : '',
      option.color || option.hex ? `<ids-color ${option.hex ? `hex="${option.hex}"` : `color="${option.color}"`}></ids-color>` : '',
      option.isCheckbox
        ? `<ids-checkbox no-margin no-animation class="justify-center multiselect-checkbox multiselect-loaded"${option.selected ? ' checked' : ''} ${this.skipSanitize ? 'skip-sanitize' : ''}></ids-checkbox><ids-text overflow="ellipsis" ${originalLabelTooltip}>${option.label || ''}</ids-text>`
        : label
    ].filter((tmpl) => tmpl.length).join('');

    return `<ids-list-box-option
      ${this.#isMultiSelect ? 'class="multiselect-option multiselect-loaded"' : ''}
      ${!this.#isMultiSelect && option.icon ? 'class="icon-option"' : ''}
      ${this.multiColumn ? 'class="multi-column"' : ''}
      ${option.id ? `id=${option.id}` : ''}
      ${option.value ? `value="${option.value}"` : ''}
      ${option.tooltip ? `tooltip="${option.tooltip}"` : ''}
      ${tooltipAttr}
      ${option.groupLabel ? 'group-label' : ''}
      ${option.description ? 'description' : ''}>${content}</ids-list-box-option>`;
  }

  /**
   * Helper to get group index for given option index in the options list
   * @param {Array<number>} groupLabels group label indexes in the options list
   * @param {number} optionIndex option index in the options list
   * @returns {number} group label index for given option
   */
  #getGroupIndex(groupLabels: Array<number>, optionIndex: number) {
    return groupLabels.reduce((initialIndex: number, groupIndex: number, index: number) => {
      if (groupIndex < optionIndex && (groupLabels[index + 1] > optionIndex || !groupLabels[index + 1])) {
        return groupIndex;
      }

      return initialIndex;
    }, -1);
  }

  /**
   * Helper to get group option for given option index in the options list
   * @param {number} optionIndex option index in the options list
   * @returns {IdsDropdownOption | undefined} group label for given option index
   */
  #getGroupLabelOption(optionIndex: number): IdsDropdownOption | undefined {
    // Get group labels indexes in the all options list
    const groupLabels: Array<number> = this.optionsData.reduce(
      (result: Array<number>, option: IdsDropdownOption, index: number) => {
        if (option?.groupLabel) {
          return [...result, index];
        }

        return result;
      },
      []
    );
    const groupLabelIndex = this.#getGroupIndex(groupLabels, optionIndex);

    return this.optionsData[groupLabelIndex];
  }

  /**
   * Find matches between the input value and the dataset
   * @param {string} inputValue value of the input field
   * @returns {IdsDropdownOptions} containing matched values
   */
  #findMatches(inputValue: string): IdsDropdownOptions {
    return this.optionsData.reduce((options: Array<IdsDropdownOption>, option: IdsDropdownOption, index: number) => {
      const regex = new RegExp(`(${escapeRegExp(inputValue)})`, 'gi');

      if (option.label?.match(regex) && !option.groupLabel && !option.description) {
        const groupLabelOption = this.#getGroupLabelOption(index);
        // Check if group label option is already added to the list
        const groupLabelAdded = options.some(
          (item: IdsDropdownOption) => item.label === groupLabelOption?.label
        );

        if (groupLabelOption && !groupLabelAdded) {
          return [...options, groupLabelOption, option];
        }

        return [...options, option];
      }

      return options;
    }, []);
  }

  /**
   * Map slotted ids-list-box-option elements to the dataset
   */
  setOptionsData() {
    if (this.setOptionsDataStored && this.optionsData.length > 0) {
      return;
    }

    this.optionsData = [...this.options].map((item) => {
      const baseData: any = {
        id: item.id,
        label: item.querySelector('span')?.textContent?.trim() || item.textContent?.trim() || item.querySelector<IdsCheckbox>('ids-checkbox')?.label || '',
        value: item.getAttribute(attributes.VALUE) as string,
        tooltip: item.getAttribute(attributes.TOOLTIP) || '',
        icon: item.querySelector<IdsIcon>('ids-icon')?.icon,
        iconColor: item.querySelector<IdsIcon>('ids-icon')?.color,
        hex: item.querySelector<IdsColor>('ids-color')?.hex,
        color: item.querySelector<IdsColor>('ids-color')?.color as string,
        avatar: item.querySelector('ids-avatar')?.outerHTML,
        groupLabel: item.hasAttribute(attributes.GROUP_LABEL),
        description: item.hasAttribute(attributes.DESCRIPTION),
        isCheckbox: Boolean(item.querySelector<IdsCheckbox>('ids-checkbox')),
        selected: Boolean(item.querySelector<IdsCheckbox>('ids-checkbox')?.checked),
      };

      // For multi-column with spans, extract column data
      if (this.multiColumn && this.columnsData.length > 0) {
        const spans = item.querySelectorAll('span');
        if (spans.length >= this.columnsData.length) {
          this.columnsData.forEach((column, index) => {
            if (spans[index]) {
              baseData[column.id] = spans[index].textContent?.trim() || '';
            }
          });
        }
      }
      return baseData;
    });
  }

  #sanitizeOption(option: IdsDropdownOption): IdsDropdownOption {
    if (this.skipSanitize) {
      return option;
    }

    return ({
      ...option,
      id: this.xssSanitize(option?.id ?? '') as string,
      value: this.xssSanitize(option.value ?? '') as string,
      label: this.xssSanitize(option.label ?? '') as string,
      tooltip: this.xssSanitize(option.tooltip ?? '') as string
    });
  }

  /**
   * Pass down `validate` attribute into IdsTriggerField
   * @param {string} value The `validate` attribute
   */
  set validate(value: string | null) {
    if (value) {
      this.setAttribute(attributes.VALIDATE, value.toString());
      if (this.input) this.input.setAttribute(attributes.VALIDATE, value.toString());
    } else {
      this.removeAttribute(attributes.VALIDATE);
      if (this.input) this.input.removeAttribute(attributes.VALIDATE);
    }
  }

  /**
   * Get the validate attribute
   * @returns {string | null} the validate value
   */
  get validate(): string | null {
    return this.getAttribute(attributes.VALIDATE);
  }

  /**
   * @param {string | boolean} value true if the required indicator should be forced to display
   */
  set required(value: string | boolean | null) {
    const validate: string = this.validate ? ` ${this.validate}` : '';

    if (value) {
      this.setAttribute(attributes.REQUIRED, value.toString());

      if (!validate.includes('required')) {
        this.validate = `required${validate}`;
      }
    } else {
      this.removeAttribute(attributes.REQUIRED);

      if (validate.includes('required') && this.validate) {
        this.validate = validate.replace('required', '');
      }
    }
  }

  /**
   * Get the required state
   * @returns {string | null} the required value
   */
  get required(): string | null {
    return this.getAttribute(attributes.REQUIRED);
  }

  /**
   * Pass down `validation-events` attribute into IdsTriggerField
   * @param {string} value The `validation-events` attribute
   */
  set validationEvents(value: string) {
    if (value) {
      this.setAttribute(attributes.VALIDATION_EVENTS, value.toString());
      if (this.input) this.input.setAttribute(attributes.VALIDATION_EVENTS, value.toString());
    } else {
      this.removeAttribute(attributes.VALIDATION_EVENTS);
      if (this.input) this.input.removeAttribute(attributes.VALIDATION_EVENTS);
    }
  }

  /**
   * Get the validation events
   * @returns {string} the validation events (default 'change')
   */
  get validationEvents(): string { return this.getAttribute(attributes.VALIDATION_EVENTS) || 'change'; }

  /**
   * Configures the connected Dropdown list component
   */
  private configureDropdownList() {
    let targetNode = this.container?.querySelector<IdsDropdownList>('ids-dropdown-list');

    if (this.hasAttribute(attributes.LIST)) {
      if (this.dropdownList && this.container?.contains(this.dropdownList)) {
        this.dropdownList.remove();
        this.dropdownList = null;
      }
      const containerNode = getClosestContainerNode(this);

      // Try to find an external node, default to a built-in if we can't find one
      targetNode = containerNode.querySelector<IdsDropdownList>(`${this.getAttribute(attributes.LIST)}`);
      if (!targetNode) {
        this.removeAttribute(attributes.LIST);
        this.container?.insertAdjacentHTML('beforeend', this.templateDropdownList());
        targetNode = this.container?.querySelector<IdsDropdownList>('ids-dropdown-list');
      }
    }
    this.dropdownList = targetNode;
    if (this.dropdownList && this.value) {
      this.dropdownList.setAttribute(attributes.VALUE, this.value);
    }

    // sync size setting
    this.onSizeChange(this.size);

    if (getClosest(this, '.modal')) {
      this.dropdownList?.popup?.setAttribute(attributes.OFFSET_CONTAINER, '.modal');
    }

    this.configurePopup();
    this.attachClickEvent();

    // Configure description if present
    if (this.description && this.dropdownList) {
      this.dropdownList.description = this.description;
    }
  }

  /**
   * Sets offset container, such as a container with a `container-type
   * set which directly affects a fixed popup's position
   * @param {string|null} selector CSS selector of container
   */
  set offsetContainer(selector: string | null) {
    if (selector) {
      this.setAttribute(attributes.OFFSET_CONTAINER, selector);
      this.dropdownList?.popup?.setAttribute(attributes.OFFSET_CONTAINER, selector);
    } else {
      this.removeAttribute(attributes.OFFSET_CONTAINER);
      this.dropdownList?.popup?.removeAttribute(attributes.OFFSET_CONTAINER);
    }
  }

  /**
   * Get the offset container
   * @returns {string | null} CSS selector of container
   */
  get offsetContainer(): string | null {
    return this.getAttribute(attributes.OFFSET_CONTAINER);
  }

  /**
   * Defines the existence of an external IdsDropdownList component, and connects this IdsDropdown
   * to the component via events.
   * @param {string | null} val ID attribute name
   */
  set list(val: string | null) {
    if (val && typeof val === 'string') {
      this.setAttribute(attributes.LIST, val);
    } else {
      this.removeAttribute(attributes.LIST);
    }
    this.configureDropdownList();
  }

  /**
   * Get the list attribute
   * @returns {string | null} the list ID
   */
  get list(): string | null { return this.getAttribute(attributes.LIST); }

  /**
   * Sets the margin-block-end spacing token on the inner .ids-dropdown container.
   * Accepts token shorthand keys (e.g. 'sm', 'lg') that map to IDS spacing CSS variables.
   * @param {MarginBlockEndKey | null} value spacing token key, or null to remove
   */
  set marginBlockEnd(value: MarginBlockEndKey | null) {
    if (value && Object.prototype.hasOwnProperty.call(MARGIN_BLOCK_END_SIZES, value)) {
      this.setAttribute(attributes.MARGIN_BLOCK_END, value);
    } else {
      this.removeAttribute(attributes.MARGIN_BLOCK_END);
      value = null;
    }
    if (this.input) this.input.marginBlockEnd = value;
  }

  /**
   * Gets the margin-block-end token key
   * @returns {MarginBlockEndKey | null} the current spacing token key, or null if not set
   */
  get marginBlockEnd(): MarginBlockEndKey | null {
    const val = this.getAttribute(attributes.MARGIN_BLOCK_END) as MarginBlockEndKey | null;
    if (val && Object.prototype.hasOwnProperty.call(MARGIN_BLOCK_END_SIZES, val)) return val;
    return null;
  }

  /**
   * Sets the no margins attribute
   * @param {boolean} value The value for no margins attribute
   */
  set noMargins(value: boolean) {
    if (isValidBooleanValue(value) && stringToBool(value)) {
      this.setAttribute(attributes.NO_MARGINS, '');
      if (this.input) this.input.setAttribute(attributes.NO_MARGINS, '');
      return;
    }
    this.removeAttribute(attributes.NO_MARGINS);
    if (this.input) this.input.removeAttribute(attributes.NO_MARGINS);
  }

  /**
   * Get the no margins state
   * @returns {boolean} true if margins are disabled
   */
  get noMargins(): boolean {
    return stringToBool(this.getAttribute(attributes.NO_MARGINS));
  }

  /**
   * Handle changes to the size attribute
   * @param {string} value - The new size value
   */
  onSizeChange(value: string) {
    if (value) this.dropdownList?.setAttribute(attributes.SIZE, value);
    else this.dropdownList?.removeAttribute(attributes.SIZE);
    this.input?.setAttribute(attributes.SIZE, value || 'md');
  }

  /**
   * Set typeahead attribute
   * @param {boolean | string} value typeahead value
   */
  set typeahead(value: boolean | string) {
    const val = isValidBooleanValue(value) && stringToBool(value);

    if (val) {
      this.setAttribute(attributes.TYPEAHEAD, String(val));
      this.container?.classList.add('typeahead');
      this.setOptionsData();
    } else {
      this.setAttribute(attributes.TYPEAHEAD, 'false');
      if (!this.comboBox) {
        this.input?.setAttribute(attributes.READONLY, 'true');
      }
      this.container?.classList.remove('typeahead');
    }
  }

  /**
   * Get the typeahead attribute
   * @returns {boolean} true if typeahead is enabled (default true)
   */
  get typeahead(): boolean {
    return this.hasAttribute(attributes.TYPEAHEAD) ? stringToBool(this.getAttribute(attributes.TYPEAHEAD)) : true;
  }

  /**
   * Show no results message on typeahead
   * @param {string | boolean | null} value for show no results
   */
  set showNoResults(value: string | boolean | null) {
    const val = isValidBooleanValue(value) && stringToBool(value);

    if (val) {
      this.setAttribute(attributes.SHOW_NO_RESULTS, String(val));
    } else {
      this.removeAttribute(attributes.SHOW_NO_RESULTS);
    }
  }

  /**
   * Get the show no results state
   * @returns {boolean} true if no results message is shown (default true)
   */
  get showNoResults(): boolean {
    return this.hasAttribute(attributes.SHOW_NO_RESULTS)
      ? stringToBool(this.getAttribute(attributes.SHOW_NO_RESULTS)) : true;
  }

  /**
   * Handle changes to the clearable text setting
   * @param {string|null} val - The new clearable text value
   */
  onClearableTextChange(val: string | null) {
    if (this.dropdownList) this.dropdownList.clearableText = val;
  }

  /**
   * Handle changes to the description setting
   * @param {string|string[]|null} val - The new description value
   */
  onDescriptionChange(val: string | string[] | null) {
    if (this.dropdownList) this.dropdownList.description = val;
  }

  /**
   * Sets the placeholder attribute
   * @param {string} value - the placeholder's text
   */
  set placeholder(value: string) {
    if (value) {
      this.setAttribute(attributes.PLACEHOLDER, value);
      this.input?.setAttribute(attributes.PLACEHOLDER, value);
    } else {
      this.removeAttribute(attributes.PLACEHOLDER);
      this.input?.removeAttribute(attributes.PLACEHOLDER);
    }
  }

  /**
   * Get the placeholder attribute
   * @returns {string} the placeholder text (default '')
   */
  get placeholder(): string {
    return this.getAttribute(attributes.PLACEHOLDER) ?? '';
  }

  /**
   * Pass focus internally
   */
  focus() {
    this.input?.focus();
    // Reset scroll position to show the beginning of the text
    if (this.input?.input) {
      this.input.input.scrollLeft = 0;
    }
  }

  /**
   * Set whether or not to show loading indicator. Disabled by default
   * @param {boolean|string|null} value show-loading-indicator attribute value
   */
  set showLoadingIndicator(value: string | boolean | null) {
    const boolVal = isValidBooleanValue(value) && stringToBool(value);
    if (boolVal) {
      this.setAttribute(attributes.SHOW_LOADING_INDICATOR, boolVal.toString());
      this.input?.setAttribute(attributes.SHOW_LOADING_INDICATOR, boolVal.toString());
      if (!this.typeahead) {
        this.close(true);
      }
    } else {
      this.removeAttribute(attributes.SHOW_LOADING_INDICATOR);
      this.input?.removeAttribute(attributes.SHOW_LOADING_INDICATOR);
    }
  }

  /**
   * Get the show loading indicator state
   * @returns {boolean} true if loading indicator is shown (default false)
   */
  get showLoadingIndicator() { return stringToBool(this.getAttribute(attributes.SHOW_LOADING_INDICATOR)); }

  /**
   * Sets whether the dropdown and its components can be tabbed to
   * @param {boolean | string | null} value true to make tabbable, false to remove from tab order
   */
  set tabbable(value: boolean | string | null) {
    const boolVal = isValidBooleanValue(value) && stringToBool(value);
    if (boolVal) {
      this.setAttribute(attributes.TABBABLE, boolVal.toString());
    } else {
      this.removeAttribute(attributes.TABBABLE);
    }
    if (this.input) this.input.tabbable = boolVal;
    if (this.trigger) this.trigger.tabbable = boolVal;
  }

  /**
   * Gets whether the dropdown can be tabbed to
   * @returns {boolean} true if tabbable (default false)
   */
  get tabbable(): boolean {
    return this.hasAttribute(attributes.TABBABLE) ? stringToBool(this.getAttribute(attributes.TABBABLE)) : false;
  }

  /**
   * Triggers a `selected` event that propagates to the target element (usually an IdsDropdown)
   * @param {CustomEvent} [e] optional event handler to pass arguments
   * @returns {void}
   */
  private triggerSelectedEvent(e?: CustomEvent): void {
    let args: any;
    if (e) args = e;
    else {
      let labelText = this.selected?.textContent;
      // For module nav with avatar, use span text only
      if (this.closest('ids-module-nav') && this.hasAvatar) {
        labelText = this.selected?.querySelector('span')?.textContent?.replace(/\s+/g, ' ').trim() || labelText;
      }
      args = {
        bubbles: true,
        detail: {
          elem: this,
          label: labelText,
          value: this.value,
        }
      };
    }
    this.dispatchEvent(new CustomEvent('selected', args));
  }

  private triggerOpenEvent() {
    this.triggerEvent('open', this, {
      bubbles: true,
      detail: {
        elem: this
      }
    });
  }

  private triggerCloseEvent(doCancel?: boolean) {
    this.triggerEvent('close', this, {
      bubbles: true,
      detail: {
        elem: this,
        operation: doCancel ? 'cancel' : 'default'
      }
    });
  }

  moveFocus(forward: boolean): void {
    const focusableElements = document.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled]), ids-button:not([disabled]), ids-input:not([disabled]), ids-dropdown:not([disabled]), ids-multiselect:not([disabled])'
    ) as NodeListOf<HTMLElement>;

    let currentIndex = -1;
    for (let i = 0; i < focusableElements.length; i++) {
      if (focusableElements[i] === this) {
        currentIndex = i;
        break;
      }
    }
    if (currentIndex === -1) return;

    const targetIndex = forward ? currentIndex + 1 : currentIndex - 1;
    if (targetIndex < 0 || targetIndex >= focusableElements.length) return;

    focusableElements[targetIndex]?.focus();
  }

  /**
   * Handle changes to the dropdown icon
   * @param {string|null} val - The new icon value
   */
  onDropdownIconChange(val: string | null) {
    if (typeof val === 'string' && val.length) {
      this.replaceTriggerIcon(this.dropdownIcon || 'dropdown');
      if (this.dropdownList) this.dropdownList.dropdownIcon = this.dropdownIcon;
    }
  }

  /**
   * Handle changes to the show list item icon setting
   * @param {boolean} val - Whether to show list item icons
   */
  onShowListItemIcon(val: boolean) {
    if (!val) this.dropdownIconEl?.remove();
  }

  /**
   * Handle tooltip target detection
   * @returns {HTMLElement|SVGElement} The target element for tooltip
   */
  onTooltipTargetDetection(): HTMLElement | SVGElement {
    if (this.dropdownList?.visible) {
      return this.dropdownList?.lastHovered || this;
    }
    return this.input?.fieldContainer || this;
  }

  canTooltipShow(tooltipEl: IdsTooltip, tooltipContent: string): boolean {
    if (this.disableTooltip) {
      return false;
    }

    if (this.dropdownList?.visible) {
      return this.dropdownList?.canTooltipShow(tooltipEl, tooltipContent);
    }

    if (tooltipContent) {
      if (tooltipContent !== 'true' && tooltipContent !== this.input?.value?.trim()) return true;
      return checkOverflow(this.input?.input);
    }

    return false;
  }

  /**
   * Get the tooltip content for the dropdown
   * @returns {string} The tooltip content text
   */
  getTooltipContent(): string {
    let tooltipContent = this.textContent;

    if (this.tooltip !== 'true' && this.tooltip.length > 0) {
      tooltipContent = this.tooltip;
    }

    const targetOption = (this.dropdownList?.visible && this.dropdownList?.lastHovered)
      ? this.dropdownList.lastHovered
      : this.selectedOption;

    if (targetOption) {
      const customTooltip = targetOption.getAttribute(attributes.TOOLTIP);
      if (customTooltip) {
        tooltipContent = customTooltip;
      } else if (this.multiColumn) {
        tooltipContent = this.transformMultiKeySpans(targetOption.innerHTML || '');
      } else {
        tooltipContent = targetOption.textContent;
      }
    }

    return tooltipContent || '';
  }

  /**
   * Get the search field element
   * @returns {IdsSearchField | null} the search field element
   */
  get searchField(): IdsSearchField | null {
    return this.popup?.querySelector('ids-search-field') || null;
  }

  /**
   * Attaches IdsSearchField component to the typeahead functionality
   */
  #connectSearchField() {
    if (this.searchField) {
      this.searchField.onSearch = (value: string) => {
        this.#typeAhead(value);
      };
    }
  }

  /**
   * Gets whether HTML sanitization should be skipped for labels
   * @returns {boolean} true if sanitization should be skipped
   */
  get skipSanitize() {
    return this.hasAttribute(attributes.SKIP_SANITIZE);
  }

  set skipSanitize(val: string | boolean) {
    this.toggleAttribute(attributes.SKIP_SANITIZE, stringToBool(val));
  }

  /**
   * Finds an option element that matches the provided text or value
   * @private
   * @param {string} text - The text to match against option text content
   * @param {string} [valueText] - Optional value text to match against option value attribute
   * @returns {HTMLElement | undefined} The matching option element, or undefined if no match found
   */
  #findMatchingOption(text: string, valueText?: string) {
    const lowerCaseText = text.toLowerCase();
    const optionValueToMatch = valueText?.toLowerCase() || lowerCaseText;

    // Exact match
    let matchingOption = this.options.find((opt) => {
      const optionText = opt.textContent?.trim().toLowerCase() || '';
      const optionValue = opt.getAttribute(attributes.VALUE)?.toLowerCase() || '';
      return optionText === lowerCaseText || optionValue === optionValueToMatch;
    });

    // Partial match (starts with)
    if (!matchingOption) {
      matchingOption = this.options.find((opt) => {
        const optionText = opt.textContent?.trim().toLowerCase() || '';
        const optionValue = opt.getAttribute(attributes.VALUE)?.toLowerCase() || '';
        return optionText.startsWith(lowerCaseText) || optionValue.startsWith(optionValueToMatch);
      });
    }

    return matchingOption;
  }

  /**
   * Prevents change events from firing during initial value binding.
   * IdsTriggerField delegates shouldDispatchChangeOnValueSet() to this host.
   * @returns {boolean} false during initialization, true after
   */
  shouldDispatchChangeOnValueSet(): boolean {
    return !this.isInitializing;
  }
}
